import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { matchesProjectFilter } from '../lib/projectStatus';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import ProgressHeatmap from '@/components/ProgressHeatmap';
import SidePanel, { SidePanelSection, SidePanelActions, SidePanelDivider } from '@/components/SidePanel';
import { Plus, Calendar, Briefcase, Clock, CheckCircle2, AlertCircle, Clipboard, ClipboardList, FileSpreadsheet, Trash2, Pencil, Check, X, Square, CheckSquare, ListTodo, PlayCircle, ChevronDown, ChevronRight, Search, Sparkles, History, Lightbulb, Package, Wand2, MessageSquare, UserPlus, Users, Wifi, WifiOff, UserCheck, CalendarCheck, ShieldCheck, GripVertical, ArrowUpDown, Save, Copy, Link, ExternalLink, Mail, Settings2, BarChart3, Tags, Maximize2, Minimize2, LayoutDashboard, Layers } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import ProgressSlider from '@/components/ProgressSlider';
import SearchableCombobox from '@/components/SearchableCombobox';
import { useRealtimeTaskUpdates, useWebSocket } from '@/contexts/WebSocketContext';
import { formatDate } from '@/utils/dateFormat';

const UNIT_OPTIONS = ['No.s', 'Sq.ft', 'R.ft', 'L.ft', 'Kg', 'Ltr', 'Set', 'Pair', 'Box', 'Bag', 'Lot'];

// Format task description with bold/underlined headers
const formatDescription = (description) => {
  if (!description) return null;
  
  // Headers to format as bold and underlined
  const headers = [
    'Scope of Work',
    'Scope Of Work',
    'Material Specification',
    'Material Specifications',
    'Installation / Labour Details',
    'Installation/Labour Details',
    'Installation Details',
    'Labour Details',
    'Inclusions',
    'Inclusion',
    'Exclusions',
    'Exclusion',
    'Assumptions',
    'Assumption',
    'Notes',
    'Note'
  ];
  
  // Create regex pattern for headers (with optional bullet • or colon :)
  const headerPattern = headers.map(h => `(•\\s*${h}\\s*:|${h}\\s*:)`).join('|');
  const regex = new RegExp(`(${headerPattern})`, 'gi');
  
  // Split and process
  const parts = description.split(regex);
  
  return parts.map((part, index) => {
    if (!part) return null;
    
    // Check if this part matches a header
    const isHeader = headers.some(h => {
      const cleanPart = part.replace('•', '').replace(':', '').trim().toLowerCase();
      return cleanPart === h.toLowerCase();
    });
    
    if (isHeader || part.match(regex)) {
      const headerText = part.replace('•', '').replace(':', '').trim();
      return (
        <span key={index}>
          <br />
          <b><u>{headerText}:</u></b>{' '}
        </span>
      );
    }
    
    // Regular text - convert dashes to line breaks for readability
    return (
      <span key={index}>
        {part.split(' - ').map((segment, i) => (
          <span key={i}>
            {i > 0 && <br />}
            {i > 0 && '- '}
            {segment}
          </span>
        ))}
      </span>
    );
  });
};

// Render formatted description HTML (for dangerouslySetInnerHTML)
const formatDescriptionHTML = (description) => {
  if (!description) return '';
  
  const headers = [
    'Scope of Work', 'Scope Of Work',
    'Material Specification', 'Material Specifications',
    'Installation / Labour Details', 'Installation/Labour Details',
    'Installation Details', 'Labour Details',
    'Inclusions', 'Inclusion',
    'Exclusions', 'Exclusion',
    'Assumptions', 'Assumption',
    'Notes', 'Note'
  ];
  
  let formatted = description;
  
  // Replace headers with bold/underlined versions
  headers.forEach(header => {
    const patterns = [
      new RegExp(`•\\s*${header}\\s*:`, 'gi'),
      new RegExp(`${header}\\s*:`, 'gi'),
      new RegExp(`•\\s*${header}`, 'gi')
    ];
    patterns.forEach(pattern => {
      formatted = formatted.replace(pattern, `<br/><b><u>${header}:</u></b>`);
    });
  });
  
  // Convert dashes to line breaks for list items
  formatted = formatted.replace(/\s-\s/g, '<br/>- ');
  
  return formatted;
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-stone-100 text-stone-600', icon: Clock },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-600', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-600', icon: PlayCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-600', icon: CheckCircle2 },
  delayed: { label: 'Delayed', color: 'bg-red-100 text-red-600', icon: AlertCircle },
};

export default function Tasks() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isConnected } = useWebSocket();
  const urlStatusFilter = searchParams.get('status');
  const urlTaskId = searchParams.get('task'); // Get task ID from URL for direct navigation
  const urlEditTaskId = searchParams.get('edit'); // Get task ID to open in edit mode (from BOQ)
  const urlProjectId = searchParams.get('project'); // Get project ID from URL
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // SECURITY: Only owner/staff/labour can access admin Tasks page
  // Clients should use /client/tasks, Vendors should use /vendor/schedule
  const isAuthorized = ['owner', 'staff', 'admin', 'project_manager', 'labour'].includes(user.role);
  
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]); // P1: Activities from Schedule merge
  const [projectVendors, setProjectVendors] = useState([]); // Vendors filtered by selected project
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkCategoryData, setBulkCategoryData] = useState({ category_group: '', category_code: '' });
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  
  // Assign Vendor Dialog state
  const [assignVendorDialogOpen, setAssignVendorDialogOpen] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [availableVendorsForTask, setAvailableVendorsForTask] = useState([]);
  
  // P0: Client Approval Dialog state
  const [clientApprovalDialogOpen, setClientApprovalDialogOpen] = useState(false);
  const [taskForApproval, setTaskForApproval] = useState(null);
  const [approvalMessage, setApprovalMessage] = useState('');
  
  // P2: Bulk Assign Vendors Dialog state (for multiple tasks)
  const [bulkAssignVendorsDialogOpen, setBulkAssignVendorsDialogOpen] = useState(false);
  const [bulkSelectedVendorIds, setBulkSelectedVendorIds] = useState([]);
  // iter-386u-e — Assign Vendor by Category modal.
  const [assignByCategoryOpen, setAssignByCategoryOpen] = useState(false);
  const [categoryAssignVendorId, setCategoryAssignVendorId] = useState('');
  const [categoryAssignSaving, setCategoryAssignSaving] = useState(false);
  const [bulkAvailableVendors, setBulkAvailableVendors] = useState([]);
  
  // Filter states
  const [selectedProject, setSelectedProject] = useState('all');
  // iter-338z8: Active/Inactive project filter — matches the Projects page.
  const [projectActiveFilter, setProjectActiveFilter] = useState('active'); // 'all' | 'active' | 'inactive'
  const [statusFilter, setStatusFilter] = useState(urlStatusFilter || 'all');
  const [viewTab, setViewTab] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'schedule' - merged admin view
  
  // Task Reordering & Sorting (like Gantt Chart)
  const [showReorderMode, setShowReorderMode] = useState(false);
  const [sortBy, setSortBy] = useState('custom'); // custom, start_date, deadline, category, status
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // Collapsible project groups - all collapsed by default
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  
  // Collapsible task descriptions - collapsed by default for space optimization
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  
  // Toggle task description expansion
  const toggleDescription = (taskId) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };
  
  // Side Panel states (Notion-style)
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelMinimized, setSidePanelMinimized] = useState(false);
  const [sidePanelMode, setSidePanelMode] = useState('create'); // create, edit, view
  // selectedTask is already declared above, using it for side panel too
  
  // Quick create dialogs
  const [quickProjectDialogOpen, setQuickProjectDialogOpen] = useState(false);
  const [quickCategoryDialogOpen, setQuickCategoryDialogOpen] = useState(false);
  
  const [quickProjectData, setQuickProjectData] = useState({
    name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    budget: 0,
  });
  const [quickCategoryData, setQuickCategoryData] = useState({
    name: '',
    code: '',
    group: 'G',
    description: '',
  });
  
  // Searchable category states
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Get last used category from localStorage
  const lastCategory = JSON.parse(localStorage.getItem('lastUsedCategory') || '{}');

  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    description: '',
    assigned_to: '',
    deadline: '',
    status: 'not_started',
    quantity: 1,
    unit: 'Sq.ft',
    rate: 0,
    category_code: lastCategory.category_code || '',
    category_group: lastCategory.category_group || '',
  });

  // Multi-task entry state
  const [multiTaskEntries, setMultiTaskEntries] = useState([
    { id: 1, title: '', description: '', quantity: 1, unit: 'Sq.ft', rate: 0 }
  ]);
  const [multiTaskCommon, setMultiTaskCommon] = useState({
    project_id: '',
    category_group: '',
    category_code: '',
    deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });
  const lastUsedTaskCommon = useRef(null);

  // AI Tasks Generation state
  const [aiTasksLoading, setAiTasksLoading] = useState(false);
  const [aiTasksContext, setAiTasksContext] = useState('');

  const [bulkData, setBulkData] = useState({
    project_id: '',
    pastedText: '',
  });
  
  // Vendor Import state - Import vendor quotations as BOQ tasks with 0% default markup
  const [vendorImportDialogOpen, setVendorImportDialogOpen] = useState(false);
  const [vendorImportData, setVendorImportData] = useState({
    project_id: '',
    vendor_id: '',
    pastedText: '',
    import_type: 'paste', // 'paste' or 'manual'
  });

  // Clone BOQ state
  const [cloneBOQDialogOpen, setCloneBOQDialogOpen] = useState(false);
  const [cloneBOQData, setCloneBOQData] = useState({
    source_project_id: '',
    target_project_id: '',
    include_descriptions: true,
  });
  const [cloneBOQLoading, setCloneBOQLoading] = useState(false);

  // Vendor Quote Request state
  const [vendorQuoteDialogOpen, setVendorQuoteDialogOpen] = useState(false);
  const [vendorQuoteData, setVendorQuoteData] = useState({
    project_id: '',
    vendor_name: '',
    vendor_email: '',
    instructions: '',
    expiry_days: 7,
  });
  const [vendorQuoteLoading, setVendorQuoteLoading] = useState(false);
  const [vendorQuoteRequests, setVendorQuoteRequests] = useState([]);
  const [viewQuoteRequestsDialogOpen, setViewQuoteRequestsDialogOpen] = useState(false);

  const [updateData, setUpdateData] = useState({
    title: '',
    description: '',
    status: '',
    work_status: '',
    material_status: '',
    transport_status: '',
    quantity: 1,
    unit: 'Sq.ft', // Default to Sq.ft (P0 fix)
    rate: 0,
    task_cost: 0,
    category_code: '',
    category_group: '',
    category_name: '',
    // Date fields for calendar integration
    start_date: '',
    deadline: '',
    // Actual values (after completion)
    actual_quantity: null,
    actual_rate: null,
    actual_remarks: '',
    // Per-task markup overrides
    admin_markup_percentage: null,
    client_markup_percentage: null,
  });

  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Quick Add Category state
  const [quickAddCategoryDialogOpen, setQuickAddCategoryDialogOpen] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({ name: '', code: '', type: 'main', parentId: '' });

  // AI Description generation states
  const [aiDescriptionLoading, setAiDescriptionLoading] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  
  // AI Cost Estimation states
  const [aiCostEstimateLoading, setAiCostEstimateLoading] = useState(false);
  const [aiCostEstimate, setAiCostEstimate] = useState(null);

  // Task Intelligence - Smart Suggestions
  const [taskSuggestions, setTaskSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [materialSuggestions, setMaterialSuggestions] = useState([]);
  const [attachedMaterials, setAttachedMaterials] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const suggestionTimeoutRef = useRef(null);

  // SECURITY: Redirect unauthorized users
  useEffect(() => {
    if (!isAuthorized) {
      toast.error('Access denied. This page is for internal use only.');
      if (user.role === 'client') {
        navigate('/client/tasks');
      } else if (user.role === 'vendor') {
        navigate('/vendor/schedule');
      } else {
        navigate('/');
      }
      return;
    }
  }, [isAuthorized, navigate, user.role]);

  useEffect(() => {
    if (!isAuthorized) return;
    fetchTasks();
    fetchProjects();
    fetchCategories();
    fetchProjectTypes();
    fetchActivities(); // P1: Fetch activities for merged view
    if (user.role === 'owner' || user.role === 'staff' || user.role === 'vendor' || user.role === 'labour') {
      fetchUsers();
    }
    // Fetch vendor quote requests for admin tools
    if (user.role === 'owner' || user.role === 'staff' || user.role === 'admin') {
      fetchVendorQuoteRequests();
    }
    
    // Listen for BOQ edit saves from other tabs to auto-refresh
    let bc;
    try {
      bc = new BroadcastChannel('boq_updates');
      bc.onmessage = (event) => {
        if (event.data?.type === 'boq_saved') {
          fetchTasks();
        }
      };
    } catch (e) { /* BroadcastChannel not supported */ }
    return () => { try { bc?.close(); } catch(e) {} };
  }, [urlStatusFilter, isAuthorized]);

  // 🔔 Real-time updates: Auto-refresh when vendor updates progress (DEBOUNCED)
  const debouncedFetchRef = useRef(null);
  useRealtimeTaskUpdates(null, (update) => {
    // Debounce refetch to prevent excessive API calls
    if (debouncedFetchRef.current) {
      clearTimeout(debouncedFetchRef.current);
    }
    debouncedFetchRef.current = setTimeout(() => {
      fetchTasks();
    }, 2000); // 2 second debounce
  });

  // Handle URL task param - Auto-open specific task from Calendar navigation
  useEffect(() => {
    const taskIdToOpen = urlTaskId || urlEditTaskId;
    if (taskIdToOpen && tasks.length > 0) {
      const taskToOpen = tasks.find(t => t.id === taskIdToOpen);
      if (taskToOpen) {
        // Expand the project containing this task
        setExpandedProjects(prev => new Set([...prev, taskToOpen.project_id]));
        // Open the edit dialog for this task
        openEditDialog(taskToOpen);
        // Clear URL params after opening
        const basePath = window.location.pathname;
        navigate(basePath, { replace: true });
      }
    }
  }, [urlTaskId, urlEditTaskId, tasks, navigate]);

  // Handle URL project param - Expand specific project
  useEffect(() => {
    const taskIdToOpen = urlTaskId || urlEditTaskId;
    if (urlProjectId && !taskIdToOpen) {
      setExpandedProjects(prev => new Set([...prev, urlProjectId]));
    }
  }, [urlProjectId, urlTaskId, urlEditTaskId]);

  // Fetch project-specific vendors when project is selected in form
  useEffect(() => {
    if (formData.project_id && formData.project_id !== 'none') {
      fetchProjectVendors(formData.project_id);
    } else {
      setProjectVendors([]);
    }
  }, [formData.project_id]);

  const fetchProjectTypes = async () => {
    try {
      const response = await axios.get(`${API}/task-intelligence/project-types`);
      setProjectTypes(response.data);
    } catch (error) {
      console.error('Failed to load project types');
    }
  };

  // Debounced task suggestions fetch
  const fetchTaskSuggestions = useCallback(async (query, projectId) => {
    if (!query || query.length < 2) {
      setTaskSuggestions([]);
      return;
    }

    try {
      const params = new URLSearchParams({ query, limit: '8' });
      if (projectId && projectId !== 'none') params.append('project_id', projectId);
      
      const response = await axios.get(`${API}/task-intelligence/suggestions?${params}`);
      setTaskSuggestions(response.data);
      if (response.data.length > 0) setSuggestionsOpen(true);
    } catch (error) {
      console.error('Failed to fetch suggestions');
    }
  }, []);

  // Debounced material suggestions
  const fetchMaterialSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setMaterialSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(`${API}/task-intelligence/material-suggestions?query=${encodeURIComponent(query)}&limit=5`);
      setMaterialSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch material suggestions');
    }
  }, []);

  // Handle task name change with debounced suggestions
  const handleTaskNameChange = (value) => {
    setFormData({ ...formData, title: value });
    
    // Clear previous timeout
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }
    
    // Debounce the suggestion fetch
    suggestionTimeoutRef.current = setTimeout(() => {
      fetchTaskSuggestions(value, formData.project_id);
      fetchMaterialSuggestions(value);
    }, 300);
  };

  // Apply suggestion to form
  const applySuggestion = (suggestion) => {
    // Find the matching category code from category_name if present
    let categoryCode = formData.category_code;
    if (suggestion.category_group && suggestion.category_name) {
      const mainCat = categories.find(g => g.group === suggestion.category_group);
      if (mainCat && mainCat.categories) {
        const subCat = mainCat.categories.find(c => c.name === suggestion.category_name || c.code === suggestion.category_name);
        if (subCat) {
          categoryCode = subCat.code;
        }
      }
    }
    
    setFormData({
      ...formData,
      title: suggestion.task_name,
      description: suggestion.description || '',
      unit: suggestion.unit || formData.unit,
      category_group: suggestion.category_group || formData.category_group,
      category_code: categoryCode,
    });
    
    // If suggestion has materials, add them
    if (suggestion.materials && suggestion.materials.length > 0) {
      setAttachedMaterials(suggestion.materials);
    }
    
    setSuggestionsOpen(false);
    setTaskSuggestions([]);
  };

  // Attach material to task
  const attachMaterial = (material) => {
    if (!attachedMaterials.find(m => m.id === material.id)) {
      setAttachedMaterials([...attachedMaterials, material]);
      
      // Extend description with material info
      const materialInfo = `\n• Material: ${material.name} (${material.category})`;
      if (!formData.description.includes(material.name)) {
        setFormData({
          ...formData,
          description: formData.description + materialInfo
        });
      }
    }
    setMaterialSuggestions([]);
  };

  // Remove attached material
  const removeMaterial = (materialId) => {
    setAttachedMaterials(attachedMaterials.filter(m => m.id !== materialId));
  };

  // Generate AI description for task
  const generateAIDescription = async (isUpdate = false) => {
    const taskTitle = isUpdate ? updateData.title : formData.title;
    if (!taskTitle) {
      toast.error('Please enter a task title first');
      return;
    }
    
    setAiDescriptionLoading(true);
    try {
      const categoryGroup = isUpdate ? updateData.category_group : formData.category_group;
      const categoryCode = isUpdate ? updateData.category_code : formData.category_code;
      const categoryInfo = categories.find(g => g.group === categoryGroup);
      const subCat = categoryInfo?.categories?.find(c => c.code === categoryCode);
      
      const response = await axios.post(`${API}/tasks/ai-description`, {
        task_title: taskTitle,
        category_name: subCat?.name || categoryInfo?.group_name || '',
        project_type: projects.find(p => p.id === (isUpdate ? selectedTask?.project_id : formData.project_id))?.project_type || 'Interior',
        keywords: ''
      });
      
      if (response.data.success) {
        if (isUpdate) {
          setUpdateData({ ...updateData, description: response.data.description });
        } else {
          setFormData({ ...formData, description: response.data.description });
        }
        toast.success(response.data.ai_assisted ? 'AI description generated!' : 'Template description added');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI description');
    } finally {
      setAiDescriptionLoading(false);
    }
  };

  // AI Cost Estimation - Estimates cost range based on task description
  const generateAICostEstimate = async () => {
    const isEdit = sidePanelMode === 'edit' || updateDialogOpen;
    const data = isEdit ? updateData : formData;
    const taskTitle = data.title;
    const description = data.description;
    const quantity = data.quantity;
    const unit = data.unit;
    
    if (!taskTitle && !description) {
      toast.error('Please enter a task title or description first');
      return;
    }
    
    setAiCostEstimateLoading(true);
    setAiCostEstimate(null);
    
    try {
      const projectId = isEdit ? selectedTask?.project_id : data.project_id;
      const response = await axios.post(`${API}/ai/estimate-task-cost`, {
        task_title: taskTitle,
        description: description,
        quantity: quantity,
        unit: unit,
        project_type: projects.find(p => p.id === projectId)?.project_type || 'Interior',
      });
      
      if (response.data.success) {
        setAiCostEstimate(response.data);
        if (response.data.suggested_rate) {
          if (isEdit) {
            toast.success(`AI suggested rate: ₹${response.data.suggested_rate.toLocaleString()} per ${unit}. Click "Apply" to use it.`);
          } else if (data.rate === 0) {
            setFormData(prev => ({ ...prev, rate: response.data.suggested_rate }));
            toast.success(`AI suggested rate: ₹${response.data.suggested_rate.toLocaleString()} per ${unit}`);
          } else {
            toast.success('AI cost estimate generated!');
          }
        }
      }
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to generate cost estimate';
      toast.error(errMsg);
      setAiCostEstimate({
        success: true,
        min_rate: 0,
        max_rate: 0,
        suggested_rate: 0,
        confidence: 'low',
        notes: 'Unable to generate AI estimate. Please enter rate manually.'
      });
    } finally {
      setAiCostEstimateLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // UNIFIED CATEGORY SOURCE: Use BOQ Categories as single source of truth
      // NOTE: This endpoint requires authentication
      const response = await axios.get(`${API}/boq-categories/for-tasks`);
      setCategories(response.data);
      console.log('Loaded BOQ categories:', response.data?.length || 0);
    } catch (error) {
      console.error('Failed to load BOQ categories:', error.response?.status);
    }
  };

  const fetchTasks = async () => {
    try {
      const url = urlStatusFilter ? `${API}/tasks?status=${urlStatusFilter}` : `${API}/tasks`;
      const response = await axios.get(url);
      setTasks(response.data);
      setSelectedTasks(new Set()); // Clear selection on refresh
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  // P1: Fetch activities for merged view
  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to load activities');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const vendorsAndLabour = response.data.filter((u) => u.role === 'vendor' || u.role === 'labour' || u.role === 'client');
      setUsers(vendorsAndLabour);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  // Fetch vendors assigned to a specific project (for vendor visibility restriction)
  const fetchProjectVendors = async (projectId) => {
    if (!projectId || projectId === 'none' || projectId === '') {
      setProjectVendors([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/projects/${projectId}/vendors`);
      // Add client and labour from general users for mixed assignment
      const clientsAndLabour = users.filter(u => u.role === 'client' || u.role === 'labour');
      setProjectVendors([...response.data, ...clientsAndLabour]);
    } catch (error) {
      console.error('Failed to load project vendors:', error);
      // Fallback to all vendors if project-specific fetch fails
      setProjectVendors(users.filter(u => u.role === 'vendor'));
    }
  };

  // Toggle project expansion
  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Expand/Collapse all projects
  const expandAllProjects = () => {
    setExpandedProjects(new Set(Object.keys(groupedByProject)));
  };

  const collapseAllProjects = () => {
    setExpandedProjects(new Set());
  };

  // Quick create project
  const handleQuickCreateProject = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/projects`, {
        ...quickProjectData,
        status: 'in_progress',
        start_date: new Date(quickProjectData.start_date).toISOString(),
        end_date: new Date(quickProjectData.end_date).toISOString(),
      });
      toast.success('Project created successfully');
      setQuickProjectDialogOpen(false);
      setQuickProjectData({
        name: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        budget: 0,
      });
      fetchProjects();
      // Auto-select the new project
      setFormData(prev => ({ ...prev, project_id: response.data.id }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  // Quick create category from dropdown - Creates in BOQ Library
  const handleInlineCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    if (!formData.category_group) {
      toast.error('Please select a Main Category first');
      return;
    }

    try {
      // Find the main category ID from group code
      const mainCat = categories.find(c => c.group === formData.category_group);
      if (!mainCat || !mainCat.main_category_id) {
        toast.error('Invalid main category');
        return;
      }
      
      // Create sub-category in BOQ Library
      const response = await axios.post(`${API}/boq-categories/sub`, {
        main_category_id: mainCat.main_category_id,
        code: `${formData.category_group}${String(mainCat.categories?.length + 1 || 1).padStart(2, '0')}`,
        name: newCategoryName.trim(),
        display_order: (mainCat.categories?.length || 0) + 1,
        status: 'active'
      });
      toast.success('Sub-category created in BOQ Library');
      fetchCategories();
      // Auto-select the new category
      setFormData(prev => ({ 
        ...prev, 
        category_code: response.data.code,
      }));
      setNewCategoryName('');
      setCategorySearchQuery('');
      setShowCategoryDropdown(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  // Filter categories based on search
  const getFilteredCategories = () => {
    if (!categorySearchQuery) return categories;
    
    return categories.map(group => ({
      ...group,
      categories: group.categories?.filter(cat => 
        cat.name.toLowerCase().includes(categorySearchQuery.toLowerCase()) ||
        cat.code.toLowerCase().includes(categorySearchQuery.toLowerCase())
      )
    })).filter(group => group.categories?.length > 0);
  };

  // Quick create category dialog - Creates in BOQ Library
  const handleQuickCreateCategory = async (e) => {
    e.preventDefault();
    
    if (!quickCategoryData.group) {
      toast.error('Please select a Main Category');
      return;
    }
    
    try {
      // Find the main category ID from group code
      const mainCat = categories.find(c => c.group === quickCategoryData.group);
      if (!mainCat || !mainCat.main_category_id) {
        toast.error('Invalid main category. Please select from BOQ Categories.');
        return;
      }
      
      // Create sub-category in BOQ Library
      const response = await axios.post(`${API}/boq-categories/sub`, {
        main_category_id: mainCat.main_category_id,
        code: `${quickCategoryData.group}${String(mainCat.categories?.length + 1 || 1).padStart(2, '0')}`,
        name: quickCategoryData.name,
        display_order: (mainCat.categories?.length || 0) + 1,
        status: 'active'
      });
      toast.success('Sub-category created in BOQ Library');
      setQuickCategoryDialogOpen(false);
      
      // Refresh categories first
      await fetchCategories();
      
      // Auto-select the new category in both formData (single task) and multiTaskCommon (multi-task)
      const newGroup = quickCategoryData.group;
      const newCode = response.data.code;
      
      setFormData(prev => ({ 
        ...prev, 
        category_code: newCode,
        category_group: newGroup,
      }));
      
      setMultiTaskCommon(prev => ({
        ...prev,
        category_group: newGroup,
        category_code: newCode,
      }));
      
      // Reset form
      setQuickCategoryData({
        name: '',
        code: '',
        group: 'G',
        description: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  // Quick Add Category handler for the new dialog
  const handleQuickAddCategoryFromDialog = async () => {
    if (!newCategoryData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      if (newCategoryData.type === 'main') {
        if (!newCategoryData.code.trim()) {
          toast.error('Category code is required for main category');
          return;
        }
        await axios.post(`${API}/boq-categories/main`, {
          name: newCategoryData.name.trim(),
          code: newCategoryData.code.trim().toUpperCase(),
        });
        toast.success('Main category created');
      } else {
        if (!newCategoryData.parentId) {
          toast.error('Please select a main category first');
          return;
        }
        const mainCat = categories.find(c => (c.main_category_id || c.id) === newCategoryData.parentId);
        const subCode = newCategoryData.code.trim() || `${mainCat?.group || 'X'}${String((mainCat?.categories?.length || 0) + 1).padStart(2, '0')}`;
        await axios.post(`${API}/boq-categories/sub`, {
          main_category_id: newCategoryData.parentId,
          name: newCategoryData.name.trim(),
          code: subCode,
        });
        toast.success('Sub-category created');
      }
      setQuickAddCategoryDialogOpen(false);
      setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
      // Refresh categories
      await fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  // Create a header row from AI Task panel
  const handleCreateHeaderFromAI = async () => {
    if (!formData.project_id) {
      toast.error('Please select a project first');
      return;
    }
    const headerName = prompt('Enter header name (e.g., "Master Bedroom", "Kitchen Area"):');
    if (!headerName?.trim()) return;
    
    try {
      await axios.post(`${API}/tasks/bulk-import`, {
        project_id: formData.project_id,
        tasks: [{ title: headerName.trim(), item_type: 'header', quantity: 0, unit: '', rate: 0 }],
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Header "${headerName.trim()}" added`);
      fetchTasks();
    } catch {
      toast.error('Failed to create header');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // P1 FIX: Enforce category selection for BOQ items
    if (!formData.category_group) {
      toast.error('Please select a Main Category for proper BOQ grouping');
      return;
    }
    
    try {
      // FORWARD MARKUP: vendor_rate is the input, store as-is
      const vendorRate = parseFloat(formData.rate) || 0;
      const projAdminMk = projects.find(p => p.id === formData.project_id)?.admin_markup_percentage
        ?? projects.find(p => p.id === formData.project_id)?.vendor_markdown_percentage ?? 20;
      const adminRate = Math.round(vendorRate * (1 + projAdminMk / 100) * 100) / 100;
      const taskCost = formData.quantity * vendorRate;
      
      // P0 CRITICAL: Build BOQ category fields from selected category
      // This ensures new tasks have proper category IDs for BOQ sync
      let boqCategoryFields = {};
      let categoryName = '';
      let categoryGroup = '';
      const categoryCode = formData.category_code === 'none' ? '' : formData.category_code;
      
      // Find the main category group
      const mainCatGroup = categories.find(g => g.group === formData.category_group);
      if (mainCatGroup) {
        boqCategoryFields.main_category_id = mainCatGroup.main_category_id;
        boqCategoryFields.main_category_code = mainCatGroup.group;
        boqCategoryFields.main_category_name = mainCatGroup.group_name;
        categoryGroup = mainCatGroup.group;
        
        // Find the sub-category if selected
        if (categoryCode) {
          const subCat = mainCatGroup.categories?.find(c => c.code === categoryCode);
          if (subCat) {
            boqCategoryFields.sub_category_id = subCat.id;
            boqCategoryFields.sub_category_code = subCat.code;
            boqCategoryFields.sub_category_name = subCat.name;
            categoryName = subCat.name;
          }
        }
      }
      
      const payload = {
        ...formData,
        ...boqCategoryFields,  // Include BOQ category fields for proper sync
        rate: vendorRate,  // Forward Markup: vendor_rate is the base
        vendor_rate: vendorRate,
        task_cost: taskCost,
        deadline: new Date(formData.deadline).toISOString(),
        category_name: categoryName || null,
        category_group: categoryGroup || null,
        category_code: categoryCode || null,
        // Send null instead of empty string for optional fields
        assigned_to: formData.assigned_to || null,
      };
      
      await axios.post(`${API}/tasks`, payload);
      toast.success('Task created successfully');
      setSidePanelOpen(false);
      setDialogOpen(false);
      fetchTasks();
      // Save last used values for session pre-fill (shared with Create Multiple Tasks)
      lastUsedTaskCommon.current = {
        project_id: formData.project_id,
        category_group: formData.category_group,
        category_code: formData.category_code,
      };
      setFormData({
        project_id: formData.project_id,
        title: '',
        description: '',
        assigned_to: '',
        deadline: '',
        status: 'not_started',
        quantity: 1,
        unit: 'Sq.ft',
        rate: 0,
        category_code: formData.category_code,
        category_group: formData.category_group,
      });
      // Reset AI cost estimate
      setAiCostEstimate(null);
      // Reset smart suggestions state
      setTaskSuggestions([]);
      setMaterialSuggestions([]);
      setAttachedMaterials([]);
      setSuggestionsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  // Update existing task
  const handleUpdateTask = async () => {
    if (!selectedTask) return;
    
    try {
      const categoryCode = formData.category_code === 'none' ? '' : formData.category_code;
      
      // Check if vendor assignment changed
      const vendorChanged = formData.assigned_to && formData.assigned_to !== selectedTask.assigned_to;
      
      // P0 CRITICAL: Build BOQ category fields from selected category
      // This ensures task categories sync properly to BOQ worksheets
      let boqCategoryFields = {};
      
      // DEBUG: Log what we're working with
      console.log('=== handleUpdateTask DEBUG ===');
      console.log('formData.category_group:', formData.category_group);
      console.log('formData.category_code:', categoryCode);
      console.log('categories length:', categories?.length);
      
      if (formData.category_group && categories && categories.length > 0) {
        // Find the main category group
        const mainCatGroup = categories.find(g => g.group === formData.category_group);
        console.log('Found mainCatGroup:', mainCatGroup?.group, mainCatGroup?.main_category_id);
        
        if (mainCatGroup) {
          boqCategoryFields.main_category_id = mainCatGroup.main_category_id;
          boqCategoryFields.main_category_code = mainCatGroup.group;
          boqCategoryFields.main_category_name = mainCatGroup.group_name;
          
          // Find the sub-category if selected
          if (categoryCode) {
            const subCat = mainCatGroup.categories?.find(c => c.code === categoryCode);
            console.log('Found subCat:', subCat?.code, subCat?.id);
            if (subCat) {
              boqCategoryFields.sub_category_id = subCat.id;
              boqCategoryFields.sub_category_code = subCat.code;
              boqCategoryFields.sub_category_name = subCat.name;
            }
          }
        }
      } else {
        console.warn('WARNING: categories not loaded or category_group not selected!');
      }
      
      console.log('boqCategoryFields to send:', boqCategoryFields);
      
      // Update task with all fields including BOQ category fields
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        ...formData,
        ...boqCategoryFields,  // Include BOQ category fields for proper sync
        category_code: categoryCode,
        deadline: new Date(formData.deadline).toISOString(),
        task_cost: formData.quantity * formData.rate,
      });
      
      // If vendor assignment changed, use the dedicated assign-vendor endpoint for rate calculation
      if (vendorChanged && formData.task_cost > 0) {
        try {
          const vendorResponse = await axios.post(`${API}/tasks/${selectedTask.id}/assign-vendor`, {
            vendor_id: formData.assigned_to
          });
          
          // Show rate info if available
          if (vendorResponse.data?.rate_warning) {
            toast.warning(vendorResponse.data.rate_warning);
          }
          
          toast.success(`Task updated and assigned to ${vendorResponse.data.vendor_name || 'vendor'}`);
        } catch (vendorError) {
          // If assign-vendor fails, task is still updated, just without rate calculation
          console.error('Vendor assignment error:', vendorError);
          toast.info('Task updated. Vendor rate calculation may require cost first.');
        }
      } else {
        toast.success('Task updated successfully');
      }
      
      setSidePanelOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Open Assign Vendor Dialog
  const openAssignVendorDialog = async (task, e) => {
    e?.stopPropagation();
    setTaskToAssign(task);
    setSelectedVendorId(task.assigned_to || '');
    
    // Fetch vendors assigned to this task's project
    try {
      const project = projects.find(p => p.id === task.project_id);
      if (project && project.assigned_vendors) {
        const vendorsList = users.filter(u => 
          (u.role === 'vendor' || u.role === 'labour') && 
          project.assigned_vendors.includes(u.id)
        );
        setAvailableVendorsForTask(vendorsList);
      } else {
        setAvailableVendorsForTask([]);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setAvailableVendorsForTask([]);
    }
    
    setAssignVendorDialogOpen(true);
  };

  // Assign Vendor to Task
  const handleAssignVendor = async () => {
    if (!taskToAssign || !selectedVendorId) {
      toast.error('Please select a vendor');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/${taskToAssign.id}/assign-vendor`, {
        vendor_id: selectedVendorId
      });
      
      if (response.data.rate_warning) {
        toast.warning(response.data.rate_warning);
      }
      
      const vendorName = response.data.vendor_name || 'vendor';
      toast.success(`Task assigned to ${vendorName}. Vendor rate: ₹${response.data.vendor_rate?.toLocaleString()}`);
      
      setAssignVendorDialogOpen(false);
      setTaskToAssign(null);
      setSelectedVendorId('');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign vendor');
    }
  };

  // P0: Open Client Approval Dialog
  const openClientApprovalDialog = (task, e) => {
    e?.stopPropagation();
    setTaskForApproval(task);
    setApprovalMessage('');
    setClientApprovalDialogOpen(true);
  };

  // P0: Request Client Approval
  const handleRequestClientApproval = async () => {
    if (!taskForApproval) return;
    
    try {
      await axios.put(`${API}/tasks/${taskForApproval.id}/request-approval`, {
        requires_client_approval: true,
        approval_message: approvalMessage || 'Please review and approve this task.'
      });
      
      toast.success('Client approval request sent! The client will be notified.');
      setClientApprovalDialogOpen(false);
      setTaskForApproval(null);
      setApprovalMessage('');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request client approval');
    }
  };

  // P2: Open Bulk Assign Vendors Dialog
  const openBulkAssignVendorsDialog = () => {
    if (selectedTasks.size === 0) {
      toast.error('Please select at least one task');
      return;
    }
    
    // Get selected tasks
    const selectedTasksList = tasks.filter(t => selectedTasks.has(t.id));
    
    // Get unique project IDs from selected tasks
    const projectIds = [...new Set(selectedTasksList.map(t => t.project_id))];
    
    // Find vendors assigned to ANY of these projects
    const allVendors = new Set();
    projectIds.forEach(projectId => {
      const project = projects.find(p => p.id === projectId);
      if (project && project.assigned_vendors) {
        project.assigned_vendors.forEach(v => allVendors.add(v));
      }
    });
    
    const projectVendorsList = users.filter(u => 
      (u.role === 'vendor' || u.role === 'labour') && 
      allVendors.has(u.id)
    );
    
    setBulkAvailableVendors(projectVendorsList);
    setBulkSelectedVendorIds([]);
    setBulkAssignVendorsDialogOpen(true);
  };

  // P2: Handle Bulk Assign Vendors
  const handleBulkAssignVendors = async () => {
    if (bulkSelectedVendorIds.length === 0) {
      toast.error('Please select at least one vendor');
      return;
    }
    
    const selectedTasksList = tasks.filter(t => selectedTasks.has(t.id));
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const task of selectedTasksList) {
        try {
          await axios.post(`${API}/tasks/${task.id}/assign-vendors`, {
            vendor_ids: bulkSelectedVendorIds
          });
          successCount++;
        } catch (err) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Vendors assigned to ${successCount} task(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to assign ${failCount} task(s)`);
      }
      
      setBulkAssignVendorsDialogOpen(false);
      setBulkSelectedVendorIds([]);
      setSelectedTasks(new Set());
      fetchTasks();
    } catch (error) {
      toast.error('Failed to assign vendors');
    }
  };

  // iter-386u-e — Assign Vendor by Category.
  const selectedTasksArr = tasks.filter((t) => selectedTasks.has(t.id));
  const selectedTasksProjectId = (selectedTasksArr[0] || {}).project_id;
  const selectedTasksCategories = Array.from(
    new Map(
      selectedTasksArr
        .filter((t) => t.project_id === selectedTasksProjectId && t.main_category_id)
        .map((t) => [t.main_category_id, { id: t.main_category_id, name: t.main_category_name || 'Category' }]),
    ).values(),
  );
  const runAssignByCategory = async () => {
    if (!categoryAssignVendorId) { toast.error('Pick a vendor'); return; }
    if (!selectedTasksProjectId) { toast.error('Select tasks from a single project first'); return; }
    if (!selectedTasksCategories.length) { toast.error('Selection has no category info to infer from'); return; }
    setCategoryAssignSaving(true);
    try {
      const r = await axios.post(
        `${API}/projects/${selectedTasksProjectId}/assign-vendor-categories`,
        {
          vendor_id: categoryAssignVendorId,
          main_category_ids: selectedTasksCategories.map((c) => c.id),
          auto_assign_tasks: true,
        },
      );
      toast.success(`${r.data.tasks_auto_assigned || 0} task(s) auto-assigned across ${selectedTasksCategories.length} categor${selectedTasksCategories.length === 1 ? 'y' : 'ies'}`);
      setAssignByCategoryOpen(false);
      setSelectedTasks(new Set());
      setCategoryAssignVendorId('');
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Category assignment failed');
    } finally {
      setCategoryAssignSaving(false);
    }
  };

  // Toggle bulk vendor selection
  const toggleBulkVendorSelection = (vendorId) => {
    setBulkSelectedVendorIds(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  // Multi-task entry handlers
  const addTaskEntry = () => {
    setMultiTaskEntries(prev => [
      ...prev,
      { id: Date.now(), title: '', description: '', quantity: 1, unit: 'Sq.ft', rate: 0 }
    ]);
  };

  const addHeaderEntry = () => {
    const headerName = prompt('Enter header name (e.g., "Master Bedroom", "Living Room"):');
    if (!headerName?.trim()) return;
    setMultiTaskEntries(prev => [
      ...prev,
      { id: Date.now() + Math.random(), type: 'header', title: headerName.trim() }
    ]);
  };

  const removeTaskEntry = (id) => {
    if (multiTaskEntries.length > 1) {
      setMultiTaskEntries(prev => prev.filter(entry => entry.id !== id));
    }
  };

  const updateTaskEntry = (id, field, value) => {
    setMultiTaskEntries(prev => prev.map(entry => 
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  // AI Tasks Generation Handler
  const handleGenerateAITasks = async () => {
    if (!multiTaskCommon.category_group) {
      toast.error('Please select a Main Category first');
      return;
    }

    // Find main category from category group
    const mainCategoryGroup = categories.find(g => g.group === multiTaskCommon.category_group);
    if (!mainCategoryGroup) {
      toast.error('Invalid category selected');
      return;
    }

    // Get main_category_id from the group (not just 'id')
    const mainCategoryId = mainCategoryGroup.main_category_id;
    if (!mainCategoryId) {
      toast.error('Category ID not found. Please try a different category.');
      return;
    }

    // Find the sub category ID if selected
    let subCategoryId = null;
    if (multiTaskCommon.category_code) {
      const subCategory = mainCategoryGroup.categories?.find(c => c.code === multiTaskCommon.category_code);
      if (subCategory) {
        subCategoryId = subCategory.id;
      }
    }

    // Get project type from selected project
    const selectedProjectObj = projects.find(p => p.id === multiTaskCommon.project_id);
    const projectType = selectedProjectObj?.project_type || 'Commercial';

    setAiTasksLoading(true);
    try {
      const response = await axios.post(`${API}/tasks/ai-generate-tasks`, {
        main_category_id: mainCategoryId,
        sub_category_id: subCategoryId,
        project_type: projectType,
        context: aiTasksContext || `Project: ${selectedProjectObj?.name || 'New Project'}`,
        budget: selectedProjectObj?.budget || undefined
      });

      if (response.data.success && response.data.tasks) {
        // Add AI-generated tasks to the entries — use AI-suggested rates
        const newTasks = response.data.tasks
          .filter(task => task.title && task.title.trim())  // Filter blank tasks
          .map((task, idx) => ({
            id: Date.now() + idx + Math.random(),
            title: task.title,
            description: task.description,
            quantity: task.quantity || 1,
            unit: task.unit || 'No.s',
            rate: task.rate || 0  // Use AI-suggested rate
          }));

        setMultiTaskEntries(prev => [...prev, ...newTasks]);
        toast.success(`Generated ${newTasks.length} AI tasks successfully!`);
        setAiTasksContext('');
      }
    } catch (error) {
      console.error('AI Tasks generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI tasks. Please try again.');
    } finally {
      setAiTasksLoading(false);
    }
  };

  const handleMultiTaskSubmit = async (e) => {
    e.preventDefault();
    
    // Validate common fields
    if (!multiTaskCommon.project_id) {
      toast.error('Please select a project');
      return;
    }
    
    // P1 FIX: Enforce category selection for BOQ items
    if (!multiTaskCommon.category_group) {
      toast.error('Please select a Main Category for proper BOQ grouping');
      return;
    }
    
    // Filter out empty entries (but keep headers)
    const validEntries = multiTaskEntries.filter(entry => entry.type === 'header' || entry.title.trim());
    if (validEntries.filter(e => e.type !== 'header').length === 0) {
      toast.error('Please add at least one task');
      return;
    }

    // Find category details
    let categoryName = '';
    let categoryGroup = multiTaskCommon.category_group || '';
    const categoryCode = multiTaskCommon.category_code === 'none' ? '' : multiTaskCommon.category_code;
    if (categoryCode) {
      for (const group of categories) {
        const found = group.categories?.find(cat => cat.code === categoryCode);
        if (found) {
          categoryName = found.name;
          categoryGroup = group.group;
          break;
        }
      }
    }

    try {
      // FORWARD MARKUP: vendor rate is the input, store as-is
      const tasks = validEntries.map(entry => {
        if (entry.type === 'header') {
          return {
            title: entry.title,
            item_type: 'header',
            description: '',
            quantity: 0,
            unit: '',
            rate: 0,
            vendor_rate: 0,
            category_group: categoryGroup,
            category_name: categoryName,
          };
        }
        const vendorRate = parseFloat(entry.rate) || 0;
        return {
          title: entry.title,
          description: entry.description,
          quantity: parseFloat(entry.quantity) || 1,
          unit: entry.unit,
          rate: vendorRate,
          vendor_rate: vendorRate,
          category_group: categoryGroup,
          category_name: categoryName,
        };
      });

      const response = await axios.post(`${API}/tasks/bulk-import`, {
        project_id: multiTaskCommon.project_id,
        tasks: tasks,
      });

      toast.success(`Successfully created ${response.data.tasks.length} tasks`);
      setSidePanelOpen(false);
      setDialogOpen(false);
      fetchTasks();
      
      // Save last used values for pre-fill (session-based via ref)
      lastUsedTaskCommon.current = {
        project_id: multiTaskCommon.project_id,
        category_group: multiTaskCommon.category_group,
        category_code: multiTaskCommon.category_code,
      };
      
      // Reset multi-task entries but pre-fill common fields from last used
      setMultiTaskEntries([{ id: 1, title: '', description: '', quantity: 1, unit: 'Sq.ft', rate: 0 }]);
      setMultiTaskCommon({
        project_id: multiTaskCommon.project_id,
        category_group: multiTaskCommon.category_group,
        category_code: multiTaskCommon.category_code,
        deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tasks');
    }
  };

  // Open side panel for creating tasks with AI suggestions
  const openCreateTaskPanel = () => {
    // Pre-fill from last used values (session-based)
    let lastProject = '', lastCatGroup = '', lastCatCode = '';
    if (lastUsedTaskCommon.current) {
      const last = lastUsedTaskCommon.current;
      if (last.project_id && projects.find(p => p.id === last.project_id)) {
        lastProject = last.project_id;
        lastCatGroup = last.category_group || '';
        lastCatCode = last.category_code || '';
      }
    }
    
    setFormData({
      project_id: lastProject,
      title: '',
      description: '',
      assigned_to: '',
      deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'not_started',
      quantity: 1,
      unit: 'Sq.ft',
      rate: 0,
      category_code: lastCatCode,
      category_group: lastCatGroup,
    });
    setAiCostEstimate(null); // Reset AI cost estimate
    setTaskSuggestions([]);
    setMaterialSuggestions([]);
    setAttachedMaterials([]);
    setSidePanelMode('create');
    setSidePanelOpen(true);
    setSidePanelMinimized(false);
  };

  // Open side panel for viewing/editing task
  const openTaskPanel = (task, mode = 'view') => {
    setSelectedTask(task);
    // P0 CRITICAL: Load BOQ category values from task for proper category display
    // Map main_category_code to category_group for UI compatibility
    setFormData({
      project_id: task.project_id || '',
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      deadline: task.deadline ? task.deadline.split('T')[0] : '',
      status: task.status || 'not_started',
      quantity: task.quantity || 1,
      unit: task.unit || 'Sq.ft',
      rate: task.vendor_rate || task.rate || 0,
      // FORWARD MARKUP: vendor_rate is the base
      _vendorRate: task.vendor_rate || task.rate || 0,
      vendor_rate: task.vendor_rate || task.rate || 0,
      // Use BOQ category fields for accurate display
      category_code: task.sub_category_code || task.category_code || '',
      category_group: task.main_category_code || task.category_group || '',
    });
    setAiCostEstimate(null); // Reset AI cost estimate
    setSidePanelMode(mode);
    setSidePanelOpen(true);
    setSidePanelMinimized(false);
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!bulkData.project_id || !bulkData.pastedText.trim()) {
      toast.error('Please select a project and paste data');
      return;
    }

    try {
      const lines = bulkData.pastedText.trim().split('\n');
      const tasks = [];
      let currentCategory = '';
      let currentCategoryGroup = 'G';
      
      // Unit mapping: input variations -> standardized unit
      const UNIT_MAP = {
        'no.s': 'No.s', 'nos': 'No.s', 'nos.': 'No.s', 'no': 'No.s', 'numbers': 'No.s', 'pcs': 'No.s', 'pieces': 'No.s',
        'sq.ft': 'Sq.ft', 'sqft': 'Sq.ft', 'sft': 'Sq.ft', 'sq ft': 'Sq.ft', 'square feet': 'Sq.ft', 'sq.ft.': 'Sq.ft',
        'r.ft': 'R.ft', 'rft': 'R.ft', 'running feet': 'R.ft',
        'l.ft': 'L.ft', 'lft': 'L.ft', 'linear feet': 'L.ft',
        'kg': 'Kg', 'kgs': 'Kg', 'kilogram': 'Kg',
        'ltr': 'Ltr', 'litre': 'Ltr', 'liter': 'Ltr',
        'set': 'Set', 'sets': 'Set',
        'pair': 'Pair', 'pairs': 'Pair',
        'box': 'Box', 'boxes': 'Box',
        'bag': 'Bag', 'bags': 'Bag',
        'lot': 'Lot', 'lots': 'Lot',
        'lumpsum': 'Lumpsum', 'ls': 'Lumpsum', 'lump sum': 'Lumpsum',
        'mtr': 'Mtr', 'meter': 'Mtr', 'metre': 'Mtr', 'meters': 'Mtr',
        'sqm': 'Sqm', 'sq.m': 'Sqm', 'square meter': 'Sqm',
        'cum': 'Cum', 'cu.m': 'Cum', 'cubic meter': 'Cum',
        'rmt': 'Rmt',
      };
      
      // Category keywords for auto-detection
      const CATEGORY_KEYWORDS = {
        'FABRICATION': 'G', 'CIVIL': 'F', 'ELECTRICAL': 'L', 'PLUMBING': 'M',
        'CARPENTRY': 'G', 'PAINTING': 'J', 'FLOORING': 'K', 'CEILING': 'H',
        'FURNITURE': 'G', 'TILING': 'K', 'MASONRY': 'F', 'FINISHING': 'J',
      };
      
      const normalizeUnit = (val) => {
        const lowerVal = val.toLowerCase().trim().replace(/\./g, '');
        for (const [key, standardUnit] of Object.entries(UNIT_MAP)) {
          if (lowerVal === key.replace(/\./g, '')) return standardUnit;
        }
        return null;
      };
      
      const parseNumber = (val) => {
        if (!val) return null;
        let cleaned = val.replace(/₹/g, '').replace(/Rs\.?/gi, '').replace(/,/g, '').trim();
        if (cleaned === '' || isNaN(parseFloat(cleaned))) return null;
        return parseFloat(cleaned);
      };
      
      // Detect if first line is header
      const firstLine = lines[0].toLowerCase();
      const isHeader = firstLine.includes('description') || firstLine.includes('quantity') || 
                       firstLine.includes('rate') || firstLine.includes('unit') || firstLine.includes('amount');
      const startIndex = isHeader ? 1 : 0;
      
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;
        
        const parts = line.includes('\t') ? line.split('\t') : line.split(',');
        const cleanParts = parts.map(p => p?.trim() || '');
        
        // Check if this is a CATEGORY row (only first column has text, rest empty)
        const nonEmptyParts = cleanParts.filter(p => p.length > 0);
        const hasOnlyFirstColumn = nonEmptyParts.length === 1 && cleanParts[0].length > 0;
        
        // Check if it looks like a category header
        const firstCol = cleanParts[0].toUpperCase();
        let looksLikeCategory = hasOnlyFirstColumn;
        
        // Also check common category patterns
        for (const keyword of Object.keys(CATEGORY_KEYWORDS)) {
          if (firstCol.includes(keyword)) {
            looksLikeCategory = true;
            currentCategoryGroup = CATEGORY_KEYWORDS[keyword];
            break;
          }
        }
        
        if (hasOnlyFirstColumn || (looksLikeCategory && cleanParts[0].length < 60)) {
          // This is a category header row
          currentCategory = cleanParts[0];
          continue;
        }
        
        // This is a task row - BOQ Format: Description | Quantity | Unit | Rate | Amount
        let task = {
          title: '',
          description: '',
          quantity: 1,
          unit: 'Sq.ft',
          rate: 0,
          amount: 0,
          category_group: currentCategoryGroup,
          category_name: currentCategory,
        };
        
        // Skip serial number if first column is just a small number
        let mainTextIdx = 0;
        const firstNum = parseNumber(cleanParts[0]);
        if (firstNum !== null && Number.isInteger(firstNum) && firstNum < 1000 && cleanParts.length > 1) {
          mainTextIdx = 1;
        }
        
        // First meaningful column is the DESCRIPTION (long text)
        // The full text goes into description, title is extracted/shortened
        const fullDescription = cleanParts[mainTextIdx] || '';
        task.description = fullDescription;
        
        // Extract a short title from the description
        // Strategy: Use first phrase before "-" or first 60 chars
        if (fullDescription.includes('-')) {
          // Use text before first dash as title
          const firstPart = fullDescription.split('-')[0].trim();
          task.title = firstPart.length > 60 ? firstPart.substring(0, 60) + '...' : firstPart;
        } else if (fullDescription.length > 60) {
          // Truncate at word boundary
          const truncated = fullDescription.substring(0, 60);
          const lastSpace = truncated.lastIndexOf(' ');
          task.title = (lastSpace > 40 ? truncated.substring(0, lastSpace) : truncated) + '...';
        } else {
          task.title = fullDescription;
        }
        
        // If category is set, prepend category name to title for clarity
        if (currentCategory && !task.title.toLowerCase().includes(currentCategory.toLowerCase().split(' ')[0])) {
          task.title = `${currentCategory} - ${task.title}`;
        }
        
        // Check if BOQ format: Name | Quantity | Unit | Rate | Amount
        // Quantity should be numeric in the next column
        const qtyIdx = mainTextIdx + 1;
        const hasNumericQty = cleanParts.length > qtyIdx && parseNumber(cleanParts[qtyIdx]) !== null;
        
        if (hasNumericQty) {
          // BOQ Format: Description | Quantity | Unit | Rate | Amount
          task.quantity = parseNumber(cleanParts[qtyIdx]) || 1;
          
          // Next column could be unit or rate
          for (let j = qtyIdx + 1; j < cleanParts.length; j++) {
            const val = cleanParts[j];
            if (!val) continue;
            
            const mappedUnit = normalizeUnit(val);
            if (mappedUnit) {
              task.unit = mappedUnit;
              continue;
            }
            
            const numVal = parseNumber(val);
            if (numVal !== null) {
              if (task.rate === 0) {
                task.rate = numVal;
              } else if (task.amount === 0) {
                task.amount = numVal;
              }
            }
          }
        } else {
          // Old format - try to extract from remaining columns
          for (let j = mainTextIdx + 1; j < cleanParts.length; j++) {
            const val = cleanParts[j];
            if (!val) continue;
            
            const mappedUnit = normalizeUnit(val);
            if (mappedUnit) {
              task.unit = mappedUnit;
              continue;
            }
            
            const numVal = parseNumber(val);
            if (numVal !== null) {
              if (task.quantity === 1 && numVal < 100000) {
                task.quantity = numVal;
              } else if (task.rate === 0) {
                task.rate = numVal;
              } else if (task.amount === 0) {
                task.amount = numVal;
              }
            }
          }
        }
        
        // Calculate amount if not provided
        if (task.amount === 0 && task.rate > 0) {
          task.amount = task.quantity * task.rate;
        }

        if (task.title) tasks.push(task);
      }

      if (tasks.length === 0) {
        toast.error('No valid tasks found in pasted data');
        return;
      }

      console.log('Parsed tasks:', tasks);

      const response = await axios.post(`${API}/tasks/bulk-import`, {
        project_id: bulkData.project_id,
        tasks: tasks,
      });

      toast.success(`Successfully imported ${response.data.tasks.length} tasks`);
      setBulkDialogOpen(false);
      setBulkData({ project_id: '', pastedText: '' });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import tasks');
    }
  };

  // Handle Vendor Import - Import vendor quotations as BOQ tasks
  // Key differences from bulk import:
  // - Vendor is optional but tracked for reference
  // - Rates are stored as "vendor_raw_rate" (original vendor quote)
  // - Vendor markup % defaults to 0% (editable later)
  // - Does NOT affect margin engine or existing negotiated rates
  const handleVendorImport = async (e) => {
    e.preventDefault();
    
    if (!vendorImportData.project_id) {
      toast.error('Please select a project');
      return;
    }
    
    if (!vendorImportData.pastedText.trim()) {
      toast.error('Please paste vendor quotation data');
      return;
    }
    
    try {
      const lines = vendorImportData.pastedText.trim().split('\n');
      const tasks = [];
      let currentCategory = '';
      
      for (let line of lines) {
        line = line.trim();
        if (!line) continue;
        
        // Detect category headers (lines without numeric data)
        const hasNumbers = /\d+\.\d{2}|\d+/.test(line);
        const tabCount = (line.match(/\t/g) || []).length;
        
        if (!hasNumbers || tabCount < 2) {
          // This is likely a category header
          const categoryText = line.replace(/[^\w\s&]/g, '').trim();
          if (categoryText && categoryText.length > 2) {
            currentCategory = categoryText;
          }
          continue;
        }
        
        // Parse data row (tab or multiple spaces separated)
        const parts = line.split(/\t+|\s{2,}/);
        
        if (parts.length >= 2) {
          let description = parts[0].trim();
          let quantity = 1;
          let unit = 'No.s';
          let rate = 0;
          
          // Try to extract quantity, unit, rate from remaining parts
          for (let i = 1; i < parts.length; i++) {
            const part = parts[i].trim().replace(/,/g, '');
            const numVal = parseFloat(part);
            
            if (!isNaN(numVal)) {
              if (quantity === 1 && numVal > 0 && numVal < 100000) {
                quantity = numVal;
              } else if (rate === 0 && numVal > 0) {
                rate = numVal;
              }
            } else if (UNIT_OPTIONS.includes(part) || part.match(/sq\.?ft|r\.?ft|l\.?ft|nos?|kg|ltr/i)) {
              unit = part;
            }
          }
          
          // Generate short title from description
          let title = description;
          if (description.length > 60) {
            const words = description.split(' ');
            title = words.slice(0, 6).join(' ');
            if (title.length < description.length) title += '...';
          }
          
          tasks.push({
            title,
            description,
            quantity,
            unit,
            rate,
            amount: quantity * rate,
            category_name: currentCategory,
            // Vendor import specific fields
            vendor_raw_rate: rate,  // Original vendor quote
            vendor_markup_percentage: 0,  // Default 0% - editable later
            imported_from_vendor: true,
          });
        }
      }
      
      if (tasks.length === 0) {
        toast.error('No valid items found in pasted data');
        return;
      }
      
      // Call vendor import endpoint
      const response = await axios.post(`${API}/tasks/vendor-import`, {
        project_id: vendorImportData.project_id,
        vendor_id: vendorImportData.vendor_id || null,
        tasks: tasks,
      });
      
      toast.success(`Successfully imported ${response.data.tasks.length} items from vendor quotation`);
      setVendorImportDialogOpen(false);
      setVendorImportData({ project_id: '', vendor_id: '', pastedText: '', import_type: 'paste' });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import vendor quotation');
    }
  };

  // Handle Clone BOQ
  const handleCloneBOQ = async (e) => {
    e.preventDefault();
    if (!cloneBOQData.source_project_id || !cloneBOQData.target_project_id) {
      toast.error('Please select both source and target projects');
      return;
    }
    if (cloneBOQData.source_project_id === cloneBOQData.target_project_id) {
      toast.error('Source and target projects must be different');
      return;
    }
    
    setCloneBOQLoading(true);
    try {
      const response = await axios.post(
        `${API}/projects/${cloneBOQData.source_project_id}/clone-boq`,
        null,
        {
          params: {
            target_project_id: cloneBOQData.target_project_id,
            include_descriptions: cloneBOQData.include_descriptions
          }
        }
      );
      
      toast.success(`Successfully cloned ${response.data.cloned_count} tasks to the target project`);
      setCloneBOQDialogOpen(false);
      setCloneBOQData({ source_project_id: '', target_project_id: '', include_descriptions: true });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone BOQ');
    } finally {
      setCloneBOQLoading(false);
    }
  };

  // Handle Vendor Quote Request
  const handleCreateVendorQuoteRequest = async (e) => {
    e.preventDefault();
    if (!vendorQuoteData.project_id || !vendorQuoteData.vendor_name) {
      toast.error('Please fill in project and vendor name');
      return;
    }
    
    setVendorQuoteLoading(true);
    try {
      const response = await axios.post(`${API}/vendor-quote-requests`, {
        project_id: vendorQuoteData.project_id,
        vendor_name: vendorQuoteData.vendor_name,
        vendor_email: vendorQuoteData.vendor_email || undefined,  // Optional
        instructions: vendorQuoteData.instructions || undefined,
        expiry_days: vendorQuoteData.expiry_days || 7,
        task_ids: [],  // Empty = all project tasks
      });
      
      // Copy URL to clipboard (separate try-catch to not affect success flow)
      try {
        await navigator.clipboard.writeText(response.data.public_url);
        toast.success(`Vendor quote link created and copied to clipboard! Expires: ${formatDate(response.data.expiry_date)}`);
      } catch (clipboardError) {
        // Clipboard failed but request succeeded - show success with manual copy info
        console.warn('Clipboard copy failed:', clipboardError);
        toast.success(`Vendor quote link created! URL: ${response.data.public_url}`, { duration: 8000 });
      }
      
      setVendorQuoteDialogOpen(false);
      setVendorQuoteData({ project_id: '', vendor_name: '', vendor_email: '', instructions: '', expiry_days: 7 });
      fetchVendorQuoteRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create vendor quote request');
    } finally {
      setVendorQuoteLoading(false);
    }
  };

  // Fetch vendor quote requests
  const fetchVendorQuoteRequests = async () => {
    try {
      const response = await axios.get(`${API}/vendor-quote-requests`);
      setVendorQuoteRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch vendor quote requests:', error);
    }
  };

  // Delete vendor quote request
  const handleDeleteVendorQuoteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this quote request? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${API}/vendor-quote-requests/${requestId}`);
      toast.success('Quote request deleted successfully');
      fetchVendorQuoteRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote request');
    }
  };

  // Create new project for Clone BOQ
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
  const [newProjectData, setNewProjectData] = useState({
    name: '',
    client_id: '',
    budget: 0,
    start_date: '',
    end_date: '',
  });
  const [newProjectLoading, setNewProjectLoading] = useState(false);

  const handleCreateNewProjectForClone = async (e) => {
    e.preventDefault();
    if (!newProjectData.name || !newProjectData.client_id) {
      toast.error('Please fill in project name and select a client');
      return;
    }
    
    setNewProjectLoading(true);
    try {
      const response = await axios.post(`${API}/projects`, {
        name: newProjectData.name,
        client_id: newProjectData.client_id,
        budget: newProjectData.budget || 0,
        start_date: newProjectData.start_date || new Date().toISOString(),
        end_date: newProjectData.end_date || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'pending'
      });
      
      toast.success(`Project "${newProjectData.name}" created successfully`);
      setNewProjectDialogOpen(false);
      setNewProjectData({ name: '', client_id: '', budget: 0, start_date: '', end_date: '' });
      
      // Refresh projects and set the new project as target
      await fetchProjects();
      setCloneBOQData({ ...cloneBOQData, target_project_id: response.data.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setNewProjectLoading(false);
    }
  };

  // Handle task update

  // Helper: Check if category is exempt from project date restrictions (Design categories)
  const isDesignCategory = (categoryGroup, categoryName) => {
    const exemptCategories = ['design', 'design and project management', 'project management'];
    const groupLower = (categoryGroup || '').toLowerCase();
    const nameLower = (categoryName || '').toLowerCase();
    return exemptCategories.some(exempt => 
      groupLower.includes(exempt) || nameLower.includes(exempt)
    );
  };

  // Helper: Validate task dates against project dates
  const validateTaskDates = (taskStartDate, taskDeadline, projectId, categoryGroup, categoryName) => {
    // Skip validation for design categories
    if (isDesignCategory(categoryGroup, categoryName)) {
      return { valid: true };
    }
    
    const project = projects.find(p => p.id === projectId);
    if (!project || (!project.start_date && !project.end_date)) {
      return { valid: true }; // No project dates to validate against
    }
    
    const projectStart = project.start_date ? new Date(project.start_date) : null;
    const projectEnd = project.end_date ? new Date(project.end_date) : null;
    const taskStart = taskStartDate ? new Date(taskStartDate) : null;
    const taskEnd = taskDeadline ? new Date(taskDeadline) : null;
    
    const errors = [];
    
    if (projectStart && taskStart && taskStart < projectStart) {
      errors.push(`Task start date cannot be before project start (${formatDate(projectStart)})`);
    }
    
    if (projectEnd && taskEnd && taskEnd > projectEnd) {
      errors.push(`Task end date cannot be after project end (${formatDate(projectEnd)})`);
    }
    
    if (errors.length > 0) {
      return { valid: false, errors };
    }
    
    return { valid: true };
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;

    try {
      // Validate task dates against project dates (except for Design categories)
      const dateValidation = validateTaskDates(
        updateData.start_date,
        updateData.deadline,
        selectedTask.project_id,
        updateData.category_group,
        updateData.category_name
      );
      
      if (!dateValidation.valid) {
        dateValidation.errors.forEach(err => toast.error(err));
        return;
      }

      // Find category name if category_code is selected
      let categoryName = updateData.category_name || '';
      if (updateData.category_code && updateData.category_code !== 'none') {
        for (const group of categories) {
          const found = group.categories?.find(cat => cat.code === updateData.category_code);
          if (found) {
            categoryName = found.name;
            break;
          }
        }
      }
      
      const vendorRate = updateData._vendorRate ?? updateData.vendor_rate ?? updateData.rate ?? 0;
      const taskCost = updateData.quantity * vendorRate;
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        title: updateData.title,
        description: updateData.description,
        status: updateData.status,
        work_status: updateData.work_status || null,
        material_status: updateData.material_status || null,
        transport_status: updateData.transport_status || null,
        quantity: updateData.quantity,
        unit: updateData.unit,
        rate: vendorRate,
        vendor_rate: vendorRate,
        task_cost: taskCost || updateData.task_cost,
        // Category data
        category_code: updateData.category_code && updateData.category_code !== 'none' ? updateData.category_code : null,
        category_group: updateData.category_group || null,
        category_name: categoryName || null,
        // Date fields for calendar integration (P0)
        start_date: updateData.start_date || null,
        deadline: updateData.deadline || null,
        // Actual values
        actual_quantity: updateData.actual_quantity,
        actual_rate: updateData.actual_rate,
        actual_remarks: updateData.actual_remarks || null,
        // Per-task markup overrides
        admin_markup_percentage: updateData.admin_markup_percentage,
        client_markup_percentage: updateData.client_markup_percentage,
      });
      toast.success('Task updated successfully');
      setUpdateDialogOpen(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleApprove = async (taskId, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API}/tasks/${taskId}/approve`);
      toast.success('Task approved successfully');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve task');
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;
    try {
      await axios.delete(`${API}/tasks/${selectedTask.id}`);
      toast.success('Task deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleProgressChange = async (task, progress) => {
    try {
      // Auto-update status based on progress
      let newStatus = task.status;
      if (progress === 0) newStatus = 'not_started';
      else if (progress === 100) newStatus = 'completed';
      else if (progress > 0 && task.status === 'not_started') newStatus = 'in_progress';
      
      await axios.put(`${API}/tasks/${task.id}`, { 
        progress: progress,
        status: newStatus,
      });
      toast.success(`Progress updated to ${progress}%`);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update progress');
    }
  };

  const openEditDialog = (task, e) => {
    e?.stopPropagation();
    setSelectedTask(task);
    setUpdateData({
      title: task.title || '',
      description: task.description || '',
      status: task.status,
      work_status: task.work_status || '',
      material_status: task.material_status || '',
      transport_status: task.transport_status || '',
      quantity: task.quantity || 1,
      unit: task.unit || 'Sq.ft',
      rate: task.vendor_rate || task.rate || 0,
      vendor_rate: task.vendor_rate || task.rate || 0,
      _vendorRate: task.vendor_rate || task.rate || 0,
      task_cost: task.task_cost || 0,
      // Category data
      category_code: task.category_code || '',
      category_group: task.category_group || '',
      category_name: task.category_name || '',
      // Date fields for calendar integration — default to project dates if task has none
      start_date: task.start_date ? task.start_date.split('T')[0] : (projects.find(p => p.id === task.project_id)?.start_date || '').split('T')[0],
      deadline: task.deadline ? task.deadline.split('T')[0] : (projects.find(p => p.id === task.project_id)?.end_date || '').split('T')[0],
      // Actual values
      actual_quantity: task.actual_quantity || null,
      actual_rate: task.actual_rate || null,
      actual_remarks: task.actual_remarks || '',
      // Per-task markup overrides
      admin_markup_percentage: task.admin_markup_percentage ?? null,
      client_markup_percentage: task.client_markup_percentage ?? null,
    });
    setUpdateDialogOpen(true);
  };

  const openDeleteDialog = (task, e) => {
    e?.stopPropagation();
    setSelectedTask(task);
    setDeleteDialogOpen(true);
  };

  // Selection handlers
  const toggleTaskSelection = (taskId, e) => {
    e?.stopPropagation();
    const newSelection = new Set(selectedTasks);
    if (newSelection.has(taskId)) {
      newSelection.delete(taskId);
    } else {
      newSelection.add(taskId);
    }
    setSelectedTasks(newSelection);
  };

  const selectAllInSection = (sectionTasks) => {
    const newSelection = new Set(selectedTasks);
    const allSelected = sectionTasks.every(t => selectedTasks.has(t.id));
    
    if (allSelected) {
      sectionTasks.forEach(t => newSelection.delete(t.id));
    } else {
      sectionTasks.forEach(t => newSelection.add(t.id));
    }
    setSelectedTasks(newSelection);
  };

  const clearSelection = () => setSelectedTasks(new Set());

  // Generate BOQ from approved tasks
  const handleGenerateBOQ = async (projectId) => {
    try {
      const response = await axios.post(`${API}/boq-worksheets?project_id=${projectId}`);
      toast.success(`BOQ created: ${response.data.worksheet_number}`);
      // Navigate to BOQ page
      window.location.href = '/boq-worksheets';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create BOQ');
    }
  };

  // Bulk actions
  const handleBulkApprove = async () => {
    try {
      const response = await axios.post(`${API}/tasks/bulk-approve`, {
        task_ids: Array.from(selectedTasks)
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      clearSelection();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve tasks');
    }
  };

  const handleBulkReject = async () => {
    try {
      const response = await axios.post(`${API}/tasks/bulk-reject`, {
        task_ids: Array.from(selectedTasks)
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      clearSelection();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject tasks');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await axios.post(`${API}/tasks/bulk-delete`, {
        task_ids: Array.from(selectedTasks)
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      clearSelection();
      setBulkDeleteDialogOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tasks');
    }
  };

  // Bulk categorize tasks
  const handleBulkCategorize = async () => {
    if (!bulkCategoryData.category_group) {
      toast.error('Please select a main category');
      return;
    }
    
    try {
      // P0 CRITICAL: Build BOQ category fields from selected category
      // This ensures bulk categorization syncs properly to BOQ worksheets
      let boqCategoryFields = {};
      let categoryName = '';
      
      // Handle "none" value for subcategory
      const categoryCode = (bulkCategoryData.category_code && bulkCategoryData.category_code !== 'none') 
        ? bulkCategoryData.category_code 
        : null;
      
      // Find the main category group to get IDs
      const mainCatGroup = categories.find(g => g.group === bulkCategoryData.category_group);
      if (mainCatGroup) {
        boqCategoryFields.main_category_id = mainCatGroup.main_category_id;
        boqCategoryFields.main_category_code = mainCatGroup.group;
        boqCategoryFields.main_category_name = mainCatGroup.group_name;
        
        // Find the sub-category if selected
        if (categoryCode) {
          const subCat = mainCatGroup.categories?.find(c => c.code === categoryCode);
          if (subCat) {
            boqCategoryFields.sub_category_id = subCat.id;
            boqCategoryFields.sub_category_code = subCat.code;
            boqCategoryFields.sub_category_name = subCat.name;
            categoryName = subCat.name;
          }
        }
      }
      
      const taskIds = Array.from(selectedTasks);
      let successCount = 0;
      let errorCount = 0;
      
      for (const taskId of taskIds) {
        try {
          await axios.put(`${API}/tasks/${taskId}`, {
            ...boqCategoryFields,  // Include BOQ category fields for proper sync
            category_code: categoryCode,
            category_name: categoryName || null,
            category_group: bulkCategoryData.category_group,
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to update task ${taskId}:`, err);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Updated category for ${successCount} task(s) - BOQ items synced`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to update ${errorCount} task(s)`);
      }
      
      clearSelection();
      setBulkCategoryDialogOpen(false);
      setBulkCategoryData({ category_group: '', category_code: '' });
      fetchTasks();
    } catch (error) {
      console.error('Bulk categorize error:', error);
      toast.error(error.response?.data?.detail || 'Failed to categorize tasks');
    }
  };

  // Bulk Convert to BOQ - Converts multiple selected tasks into BOQ line items
  const handleBulkConvertToBOQ = async () => {
    if (selectedTasks.size === 0) {
      toast.error('No tasks selected');
      return;
    }

    const taskIds = Array.from(selectedTasks);
    
    // Get the selected tasks data
    const selectedTasksData = tasks.filter(t => taskIds.includes(t.id));
    
    // Verify all tasks belong to the same project (for BOQ grouping)
    const projectIds = [...new Set(selectedTasksData.map(t => t.project_id))];
    
    if (projectIds.length > 1) {
      toast.error('Selected tasks must belong to the same project to convert to BOQ');
      return;
    }

    if (projectIds.length === 0) {
      toast.error('Tasks must be assigned to a project');
      return;
    }

    const projectId = projectIds[0];
    
    try {
      // Build line items from selected tasks
      // Include task_cost and category_group for accurate BOQ conversion
      const lineItems = selectedTasksData.map(task => {
        // Handle header rows
        if (task.item_type === 'header') {
          return {
            item_type: 'header',
            task_title: task.title,
            description: '',
            quantity: 0,
            unit: '',
            rate: 0,
            task_cost: 0,
            source_task_id: task.id
          };
        }
        return {
          description: task.title,
          quantity: task.quantity || 1,
          unit: task.unit || 'Nos',
          category_code: task.category_code || '',
          category_group: task.category_group || (task.category_code ? task.category_code[0] : ''),
          category_name: task.category || '',
          rate: task.rate || (task.task_cost && task.quantity ? task.task_cost / task.quantity : 0) || 0,
          vendor_id: task.assigned_vendor_id || null,
          task_cost: task.task_cost || 0,
          source_task_id: task.id
        };
      });

      // Convert tasks to BOQ
      const response = await axios.post(`${API}/tasks/convert-to-boq`, {
        task_id: taskIds[0], // Use first task as reference
        project_id: projectId,
        line_items: lineItems
      });

      toast.success(`Converted ${selectedTasksData.length} task(s) to BOQ: ${response.data.items_added} line items created`);
      clearSelection();
      fetchTasks();

      // Navigate to BOQ worksheet for review
      if (response.data.worksheet_id) {
        navigate(`/boq-worksheets?worksheet=${response.data.worksheet_id}`);
      }
    } catch (error) {
      console.error('Bulk convert to BOQ error:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert tasks to BOQ');
    }
  };

  const getStatusClass = (status) => {
    return STATUS_CONFIG[status]?.color || 'bg-stone-100 text-stone-600';
  };

  const getStatusLabel = (status) => STATUS_CONFIG[status]?.label || status.replace(/_/g, ' ');

  // PERFORMANCE: Memoize filtered and sorted tasks to prevent recalculation on every render
  const filteredTasks = useMemo(() => {
    // iter-338z8: build a set of project ids visible under the current
    // Active / Inactive / All filter so tasks stay in sync with Projects.js.
    const visibleProjectIds = new Set(projects.filter((p) => {
            return matchesProjectFilter(p, projectActiveFilter);
    }).map((p) => p.id));
    return tasks.filter(task => {
      // Always include header rows in all views
      if (task.item_type === 'header') {
        const matchesProject = selectedProject === 'all' || task.project_id === selectedProject;
        const matchesActive = visibleProjectIds.has(task.project_id);
        return matchesProject && matchesActive;
      }
      
      const matchesProject = selectedProject === 'all' || task.project_id === selectedProject;
      const matchesActive = visibleProjectIds.has(task.project_id);
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      // Category filter: can filter by group (A, B, etc.) or specific code (A01, A02, etc.)
      const matchesCategory = categoryFilter === 'all' || 
        task.category_code === categoryFilter || 
        task.category_group === categoryFilter;
      
      // View tab filter for approval status
      let matchesTab = true;
      if (viewTab === 'pending_approval') {
        matchesTab = task.task_cost && !task.approved && task.status !== 'completed';
      } else if (viewTab === 'approved') {
        matchesTab = task.approved === true;
      }
      
      return matchesProject && matchesActive && matchesStatus && matchesCategory && matchesTab;
    });
  }, [tasks, projects, projectActiveFilter, selectedProject, statusFilter, categoryFilter, viewTab]);

  // PERFORMANCE: Memoize sorted tasks
  const sortedFilteredTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      switch (sortBy) {
        case 'start_date':
          const aStart = a.start_date ? new Date(a.start_date) : new Date(9999, 11, 31);
          const bStart = b.start_date ? new Date(b.start_date) : new Date(9999, 11, 31);
          return aStart - bStart;
        case 'deadline':
          const aDeadline = a.deadline ? new Date(a.deadline) : new Date(9999, 11, 31);
          const bDeadline = b.deadline ? new Date(b.deadline) : new Date(9999, 11, 31);
          return aDeadline - bDeadline;
        case 'category':
          return (a.category_code || 'ZZZ').localeCompare(b.category_code || 'ZZZ');
        case 'status':
          const statusOrder = { not_started: 0, in_progress: 1, delayed: 2, pending_approval: 3, completed: 4 };
          return (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
        case 'custom':
        default:
          // Use display_order if available, otherwise newest first (by created_at desc)
          const aOrder = a.display_order ?? Number.MAX_SAFE_INTEGER;
          const bOrder = b.display_order ?? Number.MAX_SAFE_INTEGER;
          if (aOrder !== bOrder && aOrder !== Number.MAX_SAFE_INTEGER && bOrder !== Number.MAX_SAFE_INTEGER) {
            return aOrder - bOrder;
          }
          // Newest first: sort by created_at descending
          return (b.created_at || '').localeCompare(a.created_at || '');
      }
    });
  }, [filteredTasks, sortBy]);

  // PERFORMANCE: Memoize grouped tasks
  const groupedByProject = useMemo(() => {
    return sortedFilteredTasks.reduce((acc, task) => {
      const projectId = task.project_id;
      if (!acc[projectId]) acc[projectId] = [];
      acc[projectId].push(task);
      return acc;
    }, {});
  }, [sortedFilteredTasks]);

  // P1: Filter activities by project (for Activities tab) - MEMOIZED
  const filteredActivities = useMemo(() => {
    return activities.filter(activity => {
      return selectedProject === 'all' || activity.project_id === selectedProject;
    });
  }, [activities, selectedProject]);

  // ============================================================================
  // TASK REORDERING FUNCTIONS
  // ============================================================================
  
  // Handle drag start
  const handleDragStart = (e, taskId) => {
    if (!showReorderMode) return;
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  };
  
  // Handle drag over
  const handleDragOver = (e, targetTaskId) => {
    if (!showReorderMode || !draggedTaskId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  // Handle drop - reorder tasks within a project
  const handleDrop = (e, targetTaskId, projectId) => {
    e.preventDefault();
    if (!showReorderMode || !draggedTaskId || draggedTaskId === targetTaskId) {
      setDraggedTaskId(null);
      return;
    }
    
    // Get tasks for this project
    const projectTasks = [...(groupedByProject[projectId] || [])];
    const draggedIndex = projectTasks.findIndex(t => t.id === draggedTaskId);
    const targetIndex = projectTasks.findIndex(t => t.id === targetTaskId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedTaskId(null);
      return;
    }
    
    // Reorder locally first
    const [draggedTask] = projectTasks.splice(draggedIndex, 1);
    projectTasks.splice(targetIndex, 0, draggedTask);
    
    // Update display_order for all tasks in this project
    const updatedTasks = tasks.map(task => {
      if (task.project_id === projectId) {
        const newIndex = projectTasks.findIndex(t => t.id === task.id);
        return { ...task, display_order: newIndex >= 0 ? newIndex : task.display_order };
      }
      return task;
    });
    
    setTasks(updatedTasks);
    setDraggedTaskId(null);
  };
  
  // Save task order to database
  const saveTaskOrder = async (projectId) => {
    if (!projectId || projectId === 'all') {
      toast.error('Please select a specific project to save order');
      return;
    }
    
    setSavingOrder(true);
    try {
      const projectTasks = groupedByProject[projectId] || [];
      const taskIds = projectTasks.map(t => t.id);
      
      await axios.post(`${API}/tasks/update-order`, {
        project_id: projectId,
        task_ids: taskIds
      });
      
      toast.success('Task order saved successfully');
      setShowReorderMode(false);
    } catch (error) {
      toast.error('Failed to save task order');
      console.error(error);
    } finally {
      setSavingOrder(false);
    }
  };
  
  // Get serial number for a task within its project
  const getTaskSerialNumber = (task, projectTasks) => {
    const index = projectTasks.findIndex(t => t.id === task.id);
    return index + 1;
  };

  // Calculate counts for summary cards
  // PERFORMANCE: Memoize task counts
  const taskCounts = useMemo(() => ({
    total: tasks.length,
    not_started: tasks.filter(t => t.status === 'not_started').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    pending_approval: tasks.filter(t => t.task_cost && !t.approved && t.status !== 'completed').length,
    delayed: tasks.filter(t => t.status === 'delayed').length,
  }), [tasks]);

  // PERFORMANCE: Memoize project lookup function
  const getProjectName = useCallback((projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'No Project';
  }, [projects]);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12" data-testid="loading-state">Loading...</div>
      </Layout>
    );
  }

  const renderTaskCard = (task) => {
    const project = projects.find((p) => p.id === task.project_id);
    const isSelected = selectedTasks.has(task.id);
    const canApprove = (user.role === 'owner' || user.role === 'staff') && task.task_cost && !task.approved;
    
    return (
      <div
        key={task.id}
        className={`border p-5 hover:shadow-hover transition-all rounded-xl relative ${
          task.imported_from_vendor 
            ? 'bg-amber-50 border-amber-200 border-l-4 border-l-amber-500' 
            : isSelected ? 'bg-primary/5 border-primary' : 'bg-white border-stone-200'
        }`}
        data-testid={`task-card-${task.id}`}
      >
        <div className="flex gap-4">
          {/* Checkbox */}
          <div className="flex items-start pt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleTaskSelection(task.id)}
              onClick={(e) => e.stopPropagation()}
              data-testid={`task-checkbox-${task.id}`}
            />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-manrope text-lg font-bold text-stone-900" data-testid="task-title">
                    {task.title}
                  </h3>
                  {/* iter-386u-bt — Task→BOQ Sync Health chip */}
                  {task.boq_worksheet_id && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const focus = encodeURIComponent(task.title || task.id);
                        navigate(`/boq-worksheets/${task.boq_worksheet_id}/edit?focusItem=${focus}`);
                      }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 hover:border-emerald-400 rounded-full transition"
                      title="Open this task's row in the BOQ editor"
                      data-testid={`task-boq-link-${task.id}`}
                    >
                      Linked to BOQ · click to open
                    </button>
                  )}
                  {task.is_system_generated && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded">
                      Auto
                    </span>
                  )}
                  {task.category_code && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded" title={task.category_name}>
                      {task.category_code}
                    </span>
                  )}
                  {task.imported_from_vendor && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded" title={task.vendor_quote_tag || 'Vendor Quote'} data-testid={`vendor-tag-${task.id}`}>
                      {task.vendor_quote_tag || 'Vendor Quote'}
                    </span>
                  )}
                </div>
                {project && (
                  <div className="flex items-center gap-2 text-sm text-stone-500 mt-1">
                    <Briefcase size={14} />
                    <span data-testid="task-project">{project.name}</span>
                  </div>
                )}
              </div>
              
              {/* Action buttons */}
              <div className="flex items-center gap-2">
                {canApprove && (
                  <button
                    onClick={(e) => handleApprove(task.id, e)}
                    className="px-3 py-1 bg-green-600 text-white text-xs rounded-full hover:bg-green-700 transition-colors flex items-center gap-1"
                    data-testid={`approve-task-${task.id}`}
                  >
                    <Check size={12} />
                    Approve ₹{task.task_cost.toLocaleString()}
                  </button>
                )}
                <button
                  onClick={(e) => openAssignVendorDialog(task, e)}
                  className={`p-2 ${task.assigned_to ? 'text-green-500' : 'text-amber-500'} hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors`}
                  title={task.assigned_to ? 'Reassign Vendor' : 'Assign Vendor'}
                  data-testid={`assign-vendor-${task.id}`}
                >
                  <UserPlus size={16} />
                </button>
                {/* P0: Require Client Approval Button */}
                <button
                  onClick={(e) => openClientApprovalDialog(task, e)}
                  className={`p-2 ${task.client_approval_status === 'pending' ? 'text-orange-500' : task.client_approval_status === 'approved' ? 'text-green-500' : 'text-purple-400'} hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors`}
                  title={task.client_approval_status === 'pending' ? 'Awaiting Client Approval' : task.client_approval_status === 'approved' ? 'Client Approved' : 'Require Client Approval'}
                  data-testid={`client-approval-${task.id}`}
                >
                  <ShieldCheck size={16} />
                </button>
                <button
                  onClick={(e) => openEditDialog(task, e)}
                  className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  data-testid={`edit-task-${task.id}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={(e) => openDeleteDialog(task, e)}
                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`delete-task-${task.id}`}
                >
                  <Trash2 size={16} />
                </button>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(task.status)}`} data-testid="task-status">
                  {getStatusLabel(task.status)}
                </span>
              </div>
            </div>

            {task.description && (
              <div className="mb-3">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleDescription(task.id); }}
                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium mb-1"
                  data-testid={`toggle-timeline-desc-${task.id}`}
                >
                  <ChevronDown 
                    size={14} 
                    className={`transition-transform ${expandedDescriptions.has(task.id) ? 'rotate-180' : ''}`} 
                  />
                  {expandedDescriptions.has(task.id) ? 'Hide Description' : 'Show Description'}
                </button>
                {expandedDescriptions.has(task.id) && (
                  <div 
                    className="text-stone-600 text-sm leading-relaxed prose prose-sm max-w-none pl-4 border-l-2 border-purple-200 bg-stone-50 p-3 rounded-r-lg" 
                    data-testid="task-description"
                    dangerouslySetInnerHTML={{ __html: formatDescriptionHTML(task.description) }}
                  />
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 text-stone-500">
                <Calendar size={14} />
                <span>{formatDate(task.deadline)}</span>
              </div>
              {task.quantity && task.unit && (
                <div className="text-stone-600">
                  <span className="font-medium">Qty:</span> {task.quantity} {task.unit}
                </div>
              )}
              {task.rate > 0 && (
                <div className="text-stone-600">
                  <span className="font-medium">V.Rate:</span> ₹{task.rate.toLocaleString()}
                </div>
              )}
              {task.task_cost > 0 && (
                <div className="text-stone-600 font-semibold">
                  <span className="font-medium">Total:</span> ₹{task.task_cost.toLocaleString()}
                </div>
              )}
              {task.status === 'completed' && task.completed_at && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 size={14} />
                  <span>Completed {formatDate(task.completed_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="tasks-page">
        {/* iter-323: Project-wise Execution Heatmaps for currently filtered tasks */}
        {filteredTasks && filteredTasks.length > 0 && (() => {
          const byProject = {};
          filteredTasks.forEach((t) => {
            const pid = t.project_id || 'unassigned';
            if (!byProject[pid]) {
              const p = projects.find(p => p.id === pid);
              byProject[pid] = { name: p?.name || 'Unassigned', items: [] };
            }
            byProject[pid].items.push({
              id: t.id,
              task_title: t.title || t.description,
              task_progress: t.progress || 0,
              task_progress_source: t.progress_updated_by_role === 'vendor' ? 'vendor' : 'admin',
              is_header: false,
            });
          });
          const groups = Object.entries(byProject).slice(0, 6);
          if (!groups.length) return null;
          return (
            <div className="rounded-xl border border-stone-200 bg-white shadow-sm p-4 space-y-4" data-testid="tasks-heatmap-panel">
              {groups.map(([pid, g]) => (
                <div key={pid} data-testid={`heatmap-project-${pid}`}>
                  <ProgressHeatmap items={g.items} boqId={null} title={g.name} compact />
                </div>
              ))}
            </div>
          );
        })()}
        {/* Header - Sticky */}
        <div className="flex flex-wrap items-center justify-between gap-2 sticky top-[65px] z-30 bg-white -mx-3 sm:-mx-4 md:-mx-8 lg:-mx-12 px-3 sm:px-4 md:px-8 lg:px-12 py-2 border-b border-stone-200 shadow-sm">
          <h1 className="font-manrope text-xl md:text-2xl font-bold tracking-tight text-stone-900" data-testid="tasks-title">
            Tasks
          </h1>
          {(user.role === 'owner' || user.role === 'staff' || user.role === 'vendor' || user.role === 'labour') && (
            <div className="flex flex-wrap gap-2">
              <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2" data-testid="bulk-import-button">
                    <FileSpreadsheet size={18} />
                    Bulk Import
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl" data-testid="bulk-import-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-manrope text-2xl text-stone-900">Bulk Import from Excel</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleBulkImport} className="space-y-4">
                    <div>
                      <Label className="text-stone-900">Project</Label>
                      <SearchableCombobox
                        options={projects.map(p => ({ value: p.id, label: p.name }))}
                        value={bulkData.project_id}
                        onValueChange={(value) => setBulkData({ ...bulkData, project_id: value })}
                        placeholder="Search or select project..."
                        searchPlaceholder="Type to search projects..."
                        allowCreate={true}
                        onCreateNew={() => setQuickProjectDialogOpen(true)}
                        createNewText="+ Create New Project"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-900">Paste from Excel (BOQ Format)</Label>
                      <div className="text-sm text-stone-600 bg-blue-50 p-3 rounded-lg space-y-2 mb-3">
                        <p className="font-medium">Expected Format:</p>
                        <div className="text-xs text-stone-500 space-y-1">
                          <p><strong>Category rows</strong> (e.g., &quot;FABRICATION WORKS&quot;) will auto-group items</p>
                          <p><strong>Data rows</strong>: Description | Qty | Unit | Rate | Amount</p>
                        </div>
                        <div className="mt-2 p-2 bg-white rounded border border-blue-200 text-xs font-mono">
                          <p className="text-blue-600 font-bold">FABRICATION WORKS</p>
                          <p>Supply and installation of Fabricated Structure...  1170  Sq.ft  458  535860</p>
                        </div>
                        <div className="text-xs text-green-700 mt-2">
                          <p>✓ <strong>Description</strong> → Full text goes to Description field</p>
                          <p>✓ <strong>Title</strong> → Auto-extracted short version</p>
                          <p>✓ <strong>Category</strong> → From header row above</p>
                        </div>
                      </div>
                      <Textarea
                        rows={8}
                        placeholder="FABRICATION WORKS&#10;Supply and installation of Fabricated Structure...&#9;1170&#9;Sq.ft&#9;458&#9;535860"
                        value={bulkData.pastedText}
                        onChange={(e) => setBulkData({ ...bulkData, pastedText: e.target.value })}
                        className="font-mono text-sm bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                        data-testid="bulk-import-textarea"
                      />
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)} className="text-stone-900">Cancel</Button>
                      <Button type="submit" className="apple-button"><Clipboard size={16} className="mr-2" />Import Tasks</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Vendor Import Dialog - Import vendor quotations */}
              <Dialog open={vendorImportDialogOpen} onOpenChange={setVendorImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 border-amber-500 text-amber-600 hover:bg-amber-50" data-testid="vendor-import-button">
                    <Package size={18} />
                    Vendor Import
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl" data-testid="vendor-import-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
                      <Package className="text-amber-600" />
                      Import Vendor Quotation
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleVendorImport} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-stone-900">Project <span className="text-red-500">*</span></Label>
                        <SearchableCombobox
                          options={projects.map(p => ({ value: p.id, label: p.name }))}
                          value={vendorImportData.project_id}
                          onValueChange={(value) => setVendorImportData({ ...vendorImportData, project_id: value })}
                          placeholder="Search or select project..."
                          searchPlaceholder="Type to search projects..."
                          allowCreate={false}
                        />
                      </div>
                      <div>
                        <Label className="text-stone-900">Vendor (Optional)</Label>
                        <SearchableCombobox
                          options={users.filter(u => u.role === 'vendor').map(v => ({ value: v.id, label: v.name || v.email }))}
                          value={vendorImportData.vendor_id}
                          onValueChange={(value) => setVendorImportData({ ...vendorImportData, vendor_id: value })}
                          placeholder="Select vendor (optional)..."
                          searchPlaceholder="Type to search vendors..."
                          allowCreate={false}
                        />
                        <p className="text-xs text-stone-500 mt-1">Link items to a specific vendor for tracking</p>
                      </div>
                    </div>
                    
                    <div className="bg-amber-50 p-3 rounded-lg space-y-2 border border-amber-200">
                      <p className="font-medium text-amber-800">Vendor Import Rules:</p>
                      <ul className="text-xs text-amber-700 space-y-1">
                        <li>✓ Rates stored as <strong>raw vendor rates</strong> (original quote)</li>
                        <li>✓ Vendor markup defaults to <strong>0%</strong> (editable later)</li>
                        <li>✓ Does <strong>NOT</strong> affect your margin engine</li>
                        <li>✓ Does <strong>NOT</strong> overwrite existing negotiated rates</li>
                        <li>✓ Items saved as normal BOQ tasks</li>
                      </ul>
                    </div>
                    
                    <div>
                      <Label className="text-stone-900">Paste Vendor Quotation (Excel/PDF Format)</Label>
                      <div className="text-sm text-stone-600 bg-stone-50 p-3 rounded-lg space-y-2 mb-3">
                        <p className="font-medium">Expected Format (same as Bulk Import):</p>
                        <div className="text-xs text-stone-500 space-y-1">
                          <p><strong>Category rows</strong> (e.g., &quot;PLUMBING WORKS&quot;) will auto-group items</p>
                          <p><strong>Data rows</strong>: Description | Qty | Unit | Rate | Amount</p>
                        </div>
                        <div className="mt-2 p-2 bg-white rounded border border-stone-200 text-xs font-mono">
                          <p className="text-amber-600 font-bold">PLUMBING WORKS</p>
                          <p>CP Bath Set with Showerhead...  4  No.s  12500  50000</p>
                          <p>PVC Pipes 4 inch...  100  R.ft  85  8500</p>
                        </div>
                        <div className="text-xs text-green-700 mt-2">
                          <p>✓ <strong>Description</strong> → Full text goes to Description field</p>
                          <p>✓ <strong>Title</strong> → Auto-extracted short version</p>
                          <p>✓ <strong>Category</strong> → From header row above</p>
                        </div>
                      </div>
                      <Textarea
                        rows={8}
                        placeholder="PLUMBING WORKS&#10;CP Bath Set with Showerhead...&#9;4&#9;No.s&#9;12500&#9;50000&#10;PVC Pipes 4 inch...&#9;100&#9;R.ft&#9;85&#9;8500"
                        value={vendorImportData.pastedText}
                        onChange={(e) => setVendorImportData({ ...vendorImportData, pastedText: e.target.value })}
                        className="font-mono text-sm bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                        data-testid="vendor-import-textarea"
                      />
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button type="button" variant="outline" onClick={() => setVendorImportDialogOpen(false)} className="text-stone-900">Cancel</Button>
                      <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white">
                        <Package size={16} className="mr-2" />
                        Import Vendor Items
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Activity Schedule Link - for admin tracking vendor activities */}
              <Button 
                variant="outline" 
                className="flex items-center gap-2 text-purple-600 border-purple-200 hover:bg-purple-50"
                onClick={() => navigate('/schedule')}
                data-testid="activity-schedule-link"
              >
                <CalendarCheck size={18} />
                Activity Schedule
              </Button>

              {/* Admin Tools moved to BOQ Worksheets page (Phase 2) */}

              {/* Clone BOQ Dialog */}
              <Dialog open={cloneBOQDialogOpen} onOpenChange={setCloneBOQDialogOpen}>
                <DialogContent className="max-w-xl" data-testid="clone-boq-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
                      <Copy className="text-blue-600" />
                      Clone BOQ to Another Project
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCloneBOQ} className="space-y-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                      <p className="font-medium mb-2">This will copy:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>All tasks with titles, quantities, units</li>
                        <li>Category mappings</li>
                        <li>Descriptions (optional)</li>
                      </ul>
                      <p className="mt-2 text-blue-600"><strong>Not copied:</strong> Rates, vendor assignments, payment records</p>
                    </div>
                    <div>
                      <Label className="text-stone-900">Source Project (Copy FROM) *</Label>
                      <SearchableCombobox
                        options={projects.map(p => ({ value: p.id, label: p.name }))}
                        value={cloneBOQData.source_project_id}
                        onValueChange={(value) => setCloneBOQData({ ...cloneBOQData, source_project_id: value })}
                        placeholder="Select source project..."
                        searchPlaceholder="Type to search..."
                        allowCreate={false}
                      />
                    </div>
                    <div>
                      <Label className="text-stone-900">Target Project (Copy TO) *</Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <SearchableCombobox
                            options={projects.filter(p => p.id !== cloneBOQData.source_project_id).map(p => ({ value: p.id, label: p.name }))}
                            value={cloneBOQData.target_project_id}
                            onValueChange={(value) => setCloneBOQData({ ...cloneBOQData, target_project_id: value })}
                            placeholder="Select target project..."
                            searchPlaceholder="Type to search..."
                            allowCreate={false}
                          />
                        </div>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setNewProjectDialogOpen(true)}
                          className="text-green-600 border-green-300 hover:bg-green-50 whitespace-nowrap"
                        >
                          <Plus size={16} className="mr-1" />
                          New Project
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="include_descriptions"
                        checked={cloneBOQData.include_descriptions}
                        onCheckedChange={(checked) => setCloneBOQData({ ...cloneBOQData, include_descriptions: checked })}
                      />
                      <Label htmlFor="include_descriptions" className="text-stone-700">Include task descriptions</Label>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button type="button" variant="outline" onClick={() => setCloneBOQDialogOpen(false)} className="text-stone-900">Cancel</Button>
                      <Button type="submit" disabled={cloneBOQLoading} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {cloneBOQLoading ? 'Cloning...' : <><Copy size={16} className="mr-2" />Clone BOQ</>}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* New Project Dialog for Clone BOQ */}
              <Dialog open={newProjectDialogOpen} onOpenChange={setNewProjectDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="new-project-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                      <Plus className="text-green-600" />
                      Create New Project
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateNewProjectForClone} className="space-y-4">
                    <div>
                      <Label className="text-stone-900">Project Name *</Label>
                      <Input
                        value={newProjectData.name}
                        onChange={(e) => setNewProjectData({ ...newProjectData, name: e.target.value })}
                        placeholder="Enter project name..."
                        className="bg-white text-stone-900"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-900">Client *</Label>
                      <SearchableCombobox
                        options={users.filter(u => u.role === 'client').map(u => ({ value: u.id, label: u.name || u.email }))}
                        value={newProjectData.client_id}
                        onValueChange={(value) => setNewProjectData({ ...newProjectData, client_id: value })}
                        placeholder="Select client..."
                        searchPlaceholder="Type to search..."
                        allowCreate={false}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-stone-900">Budget (₹)</Label>
                        <Input
                          type="number"
                          value={newProjectData.budget}
                          onChange={(e) => setNewProjectData({ ...newProjectData, budget: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                          className="bg-white text-stone-900"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-900">Start Date</Label>
                        <Input
                          type="date"
                          value={newProjectData.start_date ? newProjectData.start_date.split('T')[0] : ''}
                          onChange={(e) => setNewProjectData({ ...newProjectData, start_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                          className="bg-white text-stone-900"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button type="button" variant="outline" onClick={() => setNewProjectDialogOpen(false)} className="text-stone-900">Cancel</Button>
                      <Button type="submit" disabled={newProjectLoading} className="bg-green-600 hover:bg-green-700 text-white">
                        {newProjectLoading ? 'Creating...' : <><Plus size={16} className="mr-2" />Create Project</>}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Create Vendor Quote Request Dialog */}
              <Dialog open={vendorQuoteDialogOpen} onOpenChange={setVendorQuoteDialogOpen}>
                <DialogContent className="max-w-xl" data-testid="vendor-quote-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
                      <Link className="text-green-600" />
                      Create Vendor Quote Link
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateVendorQuoteRequest} className="space-y-4">
                    <div className="bg-green-50 p-3 rounded-lg text-sm text-green-800">
                      <p>Generate a unique public link for a vendor to submit their pricing. The vendor will see the task scope and can submit their rates.</p>
                    </div>
                    <div>
                      <Label className="text-stone-900">Project *</Label>
                      <SearchableCombobox
                        options={projects.map(p => ({ value: p.id, label: p.name }))}
                        value={vendorQuoteData.project_id}
                        onValueChange={(value) => setVendorQuoteData({ ...vendorQuoteData, project_id: value })}
                        placeholder="Select project..."
                        searchPlaceholder="Type to search..."
                        allowCreate={false}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-stone-900">Vendor Name *</Label>
                        <Input
                          value={vendorQuoteData.vendor_name}
                          onChange={(e) => setVendorQuoteData({ ...vendorQuoteData, vendor_name: e.target.value })}
                          placeholder="e.g., ABC Contractors"
                          className="bg-white text-stone-900"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-900">Vendor Email <span className="text-stone-400 text-xs">(Optional)</span></Label>
                        <Input
                          type="email"
                          value={vendorQuoteData.vendor_email}
                          onChange={(e) => setVendorQuoteData({ ...vendorQuoteData, vendor_email: e.target.value })}
                          placeholder="vendor@example.com (optional - you can copy link manually)"
                          className="bg-white text-stone-900"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-stone-900">Instructions for Vendor</Label>
                      <Textarea
                        value={vendorQuoteData.instructions}
                        onChange={(e) => setVendorQuoteData({ ...vendorQuoteData, instructions: e.target.value })}
                        placeholder="Please provide your best rates for the listed items..."
                        className="bg-white text-stone-900"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="text-stone-900">Link Expiry (Days)</Label>
                      <Select 
                        value={String(vendorQuoteData.expiry_days)}
                        onValueChange={(value) => setVendorQuoteData({ ...vendorQuoteData, expiry_days: parseInt(value) })}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 days</SelectItem>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button type="button" variant="outline" onClick={() => setVendorQuoteDialogOpen(false)} className="text-stone-900">Cancel</Button>
                      <Button type="submit" disabled={vendorQuoteLoading} className="bg-green-600 hover:bg-green-700 text-white">
                        {vendorQuoteLoading ? 'Creating...' : <><Link size={16} className="mr-2" />Create Link & Copy</>}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* View Vendor Quote Requests Dialog */}
              <Dialog open={viewQuoteRequestsDialogOpen} onOpenChange={setViewQuoteRequestsDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="view-quote-requests-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
                      <Mail className="text-amber-600" />
                      Vendor Quote Requests
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    {vendorQuoteRequests.length === 0 ? (
                      <div className="text-center py-8 text-stone-500">
                        <Mail size={48} className="mx-auto mb-3 opacity-50" />
                        <p>No vendor quote requests yet</p>
                        <Button variant="outline" className="mt-3" onClick={() => { setViewQuoteRequestsDialogOpen(false); setVendorQuoteDialogOpen(true); }}>
                          Create First Request
                        </Button>
                      </div>
                    ) : (
                      vendorQuoteRequests.map((req) => (
                        <div key={req.id} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-semibold text-stone-900">{req.vendor_name}</span>
                              {req.vendor_email && (
                                <span className="text-stone-500 ml-2">({req.vendor_email})</span>
                              )}
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              req.status === 'submitted' ? 'bg-green-100 text-green-700' :
                              req.status === 'expired' ? 'bg-red-100 text-red-700' :
                              'bg-stone-100 text-stone-700'
                            }`}>
                              {req.status?.toUpperCase() || 'PENDING'}
                            </span>
                          </div>
                          <div className="text-sm text-stone-600">
                            <p><strong>Project:</strong> {req.project_name}</p>
                            <p><strong>Expires:</strong> {formatDate(req.expiry_date)}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const baseUrl = window.location.origin;
                                const url = `${baseUrl}/vendor-quote/${req.token}`;
                                try {
                                  await navigator.clipboard.writeText(url);
                                  toast.success('Link copied to clipboard!');
                                } catch (err) {
                                  toast.info(`Copy this URL: ${url}`, { duration: 8000 });
                                }
                              }}
                              className="text-blue-600 hover:bg-blue-50"
                            >
                              <Copy size={14} className="mr-1" />
                              Copy Link
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`/vendor-quote/${req.token}`, '_blank')}
                              className="text-green-600 hover:bg-green-50"
                            >
                              <ExternalLink size={14} className="mr-1" />
                              Open
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteVendorQuoteRequest(req.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} className="mr-1" />
                              Delete
                            </Button>
                            {(req.status === 'submitted' || req.status === 'has_submissions' || req.submission_count > 0) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`/vendor-quotes/compare/${req.id}`, '_blank')}
                                className="text-purple-600 hover:bg-purple-50"
                              >
                                <BarChart3 size={14} className="mr-1" />
                                Compare Quotes
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (open && !multiTaskCommon.project_id && lastUsedTaskCommon.current) {
                  // Pre-fill from last used values (session-based)
                  const last = lastUsedTaskCommon.current;
                  if (last.project_id && projects.find(p => p.id === last.project_id)) {
                    setMultiTaskCommon(prev => ({
                      ...prev,
                      project_id: last.project_id || '',
                      category_group: last.category_group || '',
                      category_code: last.category_code || '',
                    }));
                  }
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="apple-button flex items-center gap-2" data-testid="create-task-button">
                    <Plus size={20} />
                    New Tasks
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] w-[1400px] max-h-[90vh] overflow-y-auto" data-testid="create-task-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-manrope text-2xl text-stone-900">Create Multiple Tasks</DialogTitle>
                    <p className="text-sm text-stone-500">Add multiple tasks under the same category and project</p>
                  </DialogHeader>
                  <form onSubmit={handleMultiTaskSubmit} className="space-y-6">
                    {/* Common Fields - Project & Category */}
                    <div className="grid grid-cols-2 gap-6 p-5 bg-stone-50 rounded-lg">
                      <div className="space-y-2">
                        <Label className="text-stone-900 font-medium">Project *</Label>
                        <SearchableCombobox
                          options={projects.map(p => ({ value: p.id, label: p.name }))}
                          value={multiTaskCommon.project_id}
                          onValueChange={(value) => setMultiTaskCommon({ ...multiTaskCommon, project_id: value })}
                          placeholder="Search or select project..."
                          searchPlaceholder="Type to search projects..."
                          allowCreate={user.role === 'owner' || user.role === 'staff'}
                          onCreateNew={() => setQuickProjectDialogOpen(true)}
                          createNewText="+ Create New Project"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-stone-900 font-medium">Main Category *</Label>
                        <SearchableCombobox
                          options={categories.map(g => ({ value: g.group, label: `${g.group} - ${g.group_name}` }))}
                          value={multiTaskCommon.category_group}
                          onValueChange={(value) => setMultiTaskCommon({ ...multiTaskCommon, category_group: value, category_code: '' })}
                          placeholder="Search or select category..."
                          searchPlaceholder="Type to search categories..."
                          allowCreate={user.role === 'owner' || user.role === 'staff'}
                          onCreateNew={() => {
                            // Pre-select current group if one is selected
                            if (multiTaskCommon.category_group) {
                              setQuickCategoryData(prev => ({ ...prev, group: multiTaskCommon.category_group }));
                            }
                            setQuickCategoryDialogOpen(true);
                          }}
                          createNewText="+ Create New Subcategory"
                        />
                        {multiTaskCommon.category_group && (
                          <Select 
                            value={multiTaskCommon.category_code} 
                            onValueChange={(value) => setMultiTaskCommon({ ...multiTaskCommon, category_code: value })}
                          >
                            <SelectTrigger className="w-full bg-white text-stone-900 border-stone-300 mt-2">
                              <SelectValue placeholder="Select subcategory (optional)..." />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px] overflow-y-auto">
                              {(categories.find(g => g.group === multiTaskCommon.category_group)?.categories || []).map(cat => (
                                <SelectItem key={cat.code} value={cat.code}>
                                  {cat.code} - {cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {/* Task Entries - Enhanced Grid Layout */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-stone-900 font-medium text-base">Tasks ({multiTaskEntries.length})</Label>
                        <div className="flex items-center gap-2">
                          {/* AI Tasks Button */}
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            onClick={handleGenerateAITasks}
                            disabled={aiTasksLoading || !multiTaskCommon.category_group}
                            className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 text-purple-700 hover:from-purple-100 hover:to-indigo-100 hover:border-purple-300"
                            data-testid="ai-tasks-button"
                          >
                            {aiTasksLoading ? (
                              <>
                                <span className="animate-spin mr-2">⟳</span> Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles size={14} className="mr-1" /> AI Tasks
                              </>
                            )}
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={addTaskEntry} className="text-blue-600">
                            <Plus size={14} className="mr-1" /> Add Another Task
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={addHeaderEntry} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="add-header-entry-btn">
                            <LayoutDashboard size={14} className="mr-1" /> Add Header
                          </Button>
                        </div>
                      </div>
                      
                      {/* AI Context Input (appears when category is selected) */}
                      {multiTaskCommon.category_group && (
                        <div className="px-4 py-3 bg-purple-50/50 border border-purple-100 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Sparkles size={16} className="text-purple-500 flex-shrink-0" />
                            <Input
                              value={aiTasksContext}
                              onChange={(e) => setAiTasksContext(e.target.value)}
                              placeholder="Describe scope: 'False ceiling, Electrical, Plumbing, Painting' or '3BHK flat complete interior 25L budget'..."
                              className="flex-1 bg-white/80 border-purple-200 text-stone-800 placeholder:text-purple-400 text-base h-11"
                              style={{ fontSize: '17px' }}
                              data-testid="ai-context-input"
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Column Headers - Much wider columns for better visibility */}
                      <div className="grid gap-4 px-4 text-xs font-semibold text-stone-600 uppercase tracking-wide" 
                           style={{ gridTemplateColumns: '250px 1fr 90px 110px 120px 130px 50px' }}>
                        <div>Title *</div>
                        <div>Description (Scope of Work)</div>
                        <div className="text-center">Qty</div>
                        <div className="text-center">Unit</div>
                        <div className="text-right">V.Rate (₹)</div>
                        <div className="text-right">Total (₹)</div>
                        <div></div>
                      </div>

                      {/* Task Entry Rows - Significantly improved spacing and sizes */}
                      <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2">
                        {multiTaskEntries.map((entry, index) => {
                          // Header row
                          if (entry.type === 'header') {
                            return (
                              <div key={entry.id} className="flex items-center gap-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl" data-testid={`header-entry-${index}`}>
                                <LayoutDashboard size={16} className="text-amber-700 flex-shrink-0" />
                                <input
                                  value={entry.title}
                                  onChange={(e) => updateTaskEntry(entry.id, 'title', e.target.value)}
                                  className="flex-1 font-bold text-amber-900 bg-transparent border-none outline-none text-base"
                                  placeholder="Section Header..."
                                />
                                <button type="button" onClick={() => removeTaskEntry(entry.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            );
                          }
                          const entryTotal = (parseFloat(entry.quantity) || 0) * (parseFloat(entry.rate) || 0);
                          return (
                            <div key={entry.id} 
                                 className="grid gap-4 items-start p-4 bg-white border border-stone-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all"
                                 style={{ gridTemplateColumns: '250px 1fr 90px 110px 120px 130px 50px' }}>
                              {/* Title Input - Fixed width */}
                              <div>
                                <Input
                                  value={entry.title}
                                  onChange={(e) => updateTaskEntry(entry.id, 'title', e.target.value)}
                                  placeholder="Task title..."
                                  className="bg-white text-stone-900 border-stone-300 h-11 text-base"
                                  style={{ fontSize: '17px' }}
                                  data-testid={`task-title-${index}`}
                                />
                              </div>
                              {/* Description - Large Multi-line Textarea */}
                              <div>
                                <textarea
                                  value={entry.description}
                                  onChange={(e) => updateTaskEntry(entry.id, 'description', e.target.value)}
                                  placeholder="Enter detailed scope of work, specifications, materials..."
                                  className="w-full min-h-[120px] px-4 py-3 text-sm bg-white text-stone-900 border border-stone-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent leading-relaxed"
                                  data-testid={`task-description-${index}`}
                                />
                              </div>
                              {/* Quantity - Wider numeric input */}
                              <div>
                                <Input
                                  type="number"
                                  value={entry.quantity}
                                  onChange={(e) => updateTaskEntry(entry.id, 'quantity', e.target.value)}
                                  className="bg-white text-stone-900 border-stone-300 h-11 text-center text-base font-medium"
                                  min="0"
                                  step="0.01"
                                  data-testid={`task-qty-${index}`}
                                />
                              </div>
                              {/* Unit Dropdown */}
                              <div>
                                <Select value={entry.unit} onValueChange={(v) => updateTaskEntry(entry.id, 'unit', v)}>
                                  <SelectTrigger className="bg-white text-stone-900 border-stone-300 h-11">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {UNIT_OPTIONS.map((unit) => <SelectItem key={unit} value={unit}>{unit}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              {/* Rate - Wider numeric input */}
                              <div>
                                <Input
                                  type="number"
                                  value={entry.rate}
                                  onChange={(e) => updateTaskEntry(entry.id, 'rate', e.target.value)}
                                  placeholder="0"
                                  className="bg-white text-stone-900 border-stone-300 h-11 text-right text-base font-medium"
                                  min="0"
                                  step="0.01"
                                  data-testid={`task-rate-${index}`}
                                />
                              </div>
                              {/* Total - Auto-calculated, read-only */}
                              <div className="flex items-center justify-end h-11 px-4 bg-stone-100 rounded-lg border border-stone-200">
                                <span className={`text-base font-bold ${entryTotal > 0 ? 'text-green-700' : 'text-stone-400'}`}>
                                  {entryTotal > 0 ? `₹${entryTotal.toLocaleString('en-IN')}` : '₹0'}
                                </span>
                              </div>
                              {/* Delete Button */}
                              <div className="flex justify-center items-center h-11">
                                {multiTaskEntries.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeTaskEntry(entry.id)}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remove task"
                                  >
                                    <Trash2 size={20} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Summary - Enhanced */}
                    <div className="p-5 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl flex items-center justify-between border border-blue-200">
                      <span className="text-stone-700 text-base">
                        <strong className="text-blue-800 text-xl">{multiTaskEntries.filter(e => e.title.trim()).length}</strong> tasks ready to create
                      </span>
                      <span className="text-2xl font-bold text-blue-800">
                        V.Total: ₹{multiTaskEntries.reduce((sum, e) => sum + (parseFloat(e.quantity) || 0) * (parseFloat(e.rate) || 0), 0).toLocaleString('en-IN')}
                      </span>
                    </div>

                    <div className="flex gap-3 justify-end pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="text-stone-900">Cancel</Button>
                      <Button type="submit" className="apple-button">
                        <Plus size={16} className="mr-2" />
                        Create {multiTaskEntries.filter(e => e.title.trim()).length} Tasks
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Single Task with AI - Side Panel */}
              <Button onClick={openCreateTaskPanel} variant="outline" className="flex items-center gap-2" data-testid="create-single-task-btn">
                <Wand2 size={18} />
                AI Task
              </Button>
            </div>
          )}
        </div>

        {/* Bulk Action Bar - Fixed at Top (Frozen Pane like Excel) */}
        {selectedTasks.size > 0 && (
          <div 
            className="fixed top-[65px] left-0 md:left-[220px] right-0 z-[100] px-4 md:px-8 lg:px-12 py-3 bg-white/95 backdrop-blur-sm border-b border-stone-200 shadow-md transition-all"
            data-testid="bulk-action-container"
          >
            <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl flex items-center justify-between shadow-lg border border-blue-500" data-testid="bulk-action-bar">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckSquare className="text-white" size={22} />
                </div>
                <div>
                  <span className="font-bold text-white text-lg">{selectedTasks.size} task(s) selected</span>
                  <p className="text-blue-100 text-xs">Select actions below</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearSelection} 
                  className="text-white/80 hover:text-white hover:bg-white/10"
                >
                  <X size={16} className="mr-1" />
                  Clear
                </Button>
                <div className="w-px h-8 bg-white/20 mx-1" />
                <Button 
                  size="sm" 
                  onClick={() => setBulkCategoryDialogOpen(true)} 
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  <Pencil size={16} className="mr-1" />
                  Categorize
                </Button>
                
                {/* P2: Bulk Assign Vendors Button */}
                {(user.role === 'owner' || user.role === 'staff' || user.role === 'admin') && (
                  <>
                    <Button 
                      size="sm" 
                      onClick={openBulkAssignVendorsDialog} 
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                      data-testid="bulk-assign-vendors-btn"
                    >
                      <Users size={16} className="mr-1" />
                      Assign Vendors
                    </Button>
                    {/* iter-386u-e — Assign Vendor by Category (auto-assigns every task
                        in the selected categories under the selection's project). */}
                    <Button
                      size="sm"
                      onClick={() => setAssignByCategoryOpen(true)}
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                      data-testid="assign-by-category-btn"
                      title="Assign a vendor to all tasks in the selected categories"
                    >
                      <Layers size={16} className="mr-1" />
                      Assign by Category
                    </Button>
                  </>
                )}
                
                <Button 
                  size="sm" 
                  onClick={handleBulkConvertToBOQ} 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white border-0" 
                  data-testid="bulk-convert-boq-btn"
                >
                  <FileSpreadsheet size={16} className="mr-1" />
                  Convert to BOQ
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleBulkApprove} 
                  className="bg-green-500 hover:bg-green-600 text-white border-0"
                >
                  <Check size={16} className="mr-1" />
                  Approve
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleBulkReject} 
                  className="bg-orange-500 hover:bg-orange-600 text-white border-0"
                >
                  <X size={16} className="mr-1" />
                  Reject
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => setBulkDeleteDialogOpen(true)} 
                  className="bg-red-500 hover:bg-red-600 text-white border-0"
                >
                  <Trash2 size={16} className="mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Spacer when bulk action bar is visible */}
        {selectedTasks.size > 0 && <div className="h-24" />}

        {/* Edit Task Dialog */}
        <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-visible" data-testid="update-task-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-2xl text-stone-900">Edit Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label className="text-stone-900">Task Title</Label>
                <Input required className="bg-white text-stone-900 border-stone-300" value={updateData.title} onChange={(e) => setUpdateData({ ...updateData, title: e.target.value })} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-stone-900">Description</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                      className="text-stone-500 hover:text-stone-700 hover:bg-stone-50 h-7 px-2 text-xs"
                      data-testid="expand-description-btn"
                    >
                      {descriptionExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => generateAIDescription(true)}
                      disabled={aiDescriptionLoading || !updateData.title}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 px-2 text-xs"
                      data-testid="ai-generate-description-btn"
                    >
                      {aiDescriptionLoading ? (
                        <span className="flex items-center gap-1"><span className="animate-spin">⏳</span> Generating...</span>
                      ) : (
                        <span className="flex items-center gap-1"><Wand2 size={14} /> AI Generate</span>
                      )}
                    </Button>
                  </div>
                </div>
                <Textarea
                  rows={descriptionExpanded ? 12 : 4}
                  className={`bg-white text-stone-900 border-stone-300 transition-all duration-200 ${descriptionExpanded ? 'min-h-[300px]' : 'min-h-[80px]'}`}
                  value={updateData.description}
                  onChange={(e) => setUpdateData({ ...updateData, description: e.target.value })}
                  data-testid="edit-task-description"
                />
              </div>
              
              {/* Category Selection - Enhanced with Search & Quick Add */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-stone-900">Main Category</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
                        setQuickAddCategoryDialogOpen(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  <SearchableCombobox
                    options={categories.map(group => ({
                      value: group.group,
                      label: `${group.group} - ${group.group_name}`,
                    }))}
                    value={updateData.category_group || ''}
                    onValueChange={(value) => setUpdateData({ 
                      ...updateData, 
                      category_group: value,
                      category_code: '',
                      category_name: ''
                    })}
                    placeholder="Search main category..."
                    searchPlaceholder="Type to search..."
                    emptyText="No categories found"
                    allowCreate
                    onCreateNew={() => {
                      setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
                      setQuickAddCategoryDialogOpen(true);
                    }}
                    createNewText="+ Add Main Category"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-stone-900">Sub-Category</Label>
                    {updateData.category_group && (
                      <button
                        type="button"
                        onClick={() => {
                          const mainCat = categories.find(g => g.group === updateData.category_group);
                          setNewCategoryData({ 
                            name: '', 
                            code: '', 
                            type: 'sub', 
                            parentId: mainCat?.main_category_id || mainCat?.id || ''
                          });
                          setQuickAddCategoryDialogOpen(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus size={12} /> Add
                      </button>
                    )}
                  </div>
                  <SearchableCombobox
                    options={(categories.find(g => g.group === updateData.category_group)?.categories || []).map(cat => ({
                      value: cat.code,
                      label: `${cat.code} - ${cat.name}`,
                    }))}
                    value={updateData.category_code || ''}
                    onValueChange={(value) => {
                      const selectedCat = categories
                        .find(g => g.group === updateData.category_group)
                        ?.categories?.find(c => c.code === value);
                      setUpdateData({ 
                        ...updateData, 
                        category_code: value,
                        category_name: selectedCat?.name || ''
                      });
                    }}
                    placeholder={updateData.category_group ? "Search sub-category..." : "Select main first"}
                    searchPlaceholder="Type to search..."
                    emptyText="No sub-categories"
                    disabled={!updateData.category_group}
                    allowCreate={!!updateData.category_group}
                    onCreateNew={() => {
                      const mainCat = categories.find(g => g.group === updateData.category_group);
                      setNewCategoryData({ 
                        name: '', 
                        code: '', 
                        type: 'sub', 
                        parentId: mainCat?.main_category_id || mainCat?.id || ''
                      });
                      setQuickAddCategoryDialogOpen(true);
                    }}
                    createNewText="+ Add Sub-Category"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-stone-900">Status</Label>
                <Select value={updateData.status} onValueChange={(value) => setUpdateData({ ...updateData, status: value })}>
                  <SelectTrigger className="bg-white text-stone-900 border-stone-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="delayed">Delayed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Task Dates Section - P0: Admin/Vendor can edit start and end dates */}
              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/50">
                <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                  <CalendarCheck size={16} />
                  Task Schedule (Calendar Integration)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-700 text-sm">Start Date</Label>
                    <Input 
                      type="date" 
                      className="bg-white text-stone-900 border-stone-300" 
                      value={updateData.start_date || ''} 
                      onChange={(e) => setUpdateData({ ...updateData, start_date: e.target.value })} 
                      data-testid="edit-task-start-date"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-700 text-sm">End Date (Deadline) <span className="text-red-500">*</span></Label>
                    <Input 
                      type="date" 
                      className="bg-white text-stone-900 border-stone-300" 
                      value={updateData.deadline || ''} 
                      onChange={(e) => setUpdateData({ ...updateData, deadline: e.target.value })} 
                      data-testid="edit-task-deadline"
                    />
                  </div>
                </div>
                <p className="text-xs text-purple-600 mt-2">
                  These dates are displayed on the Project Calendar for execution monitoring.
                </p>
              </div>

              {/* Estimated Values Section */}
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Clipboard size={16} />
                  Estimated (From Design)
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-stone-700 text-sm">Quantity</Label>
                    <Input type="number" min="0" step="0.01" className="bg-white text-stone-900 border-stone-300" value={updateData.quantity} onChange={(e) => setUpdateData({ ...updateData, quantity: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <Label className="text-stone-700 text-sm">Unit</Label>
                    <Select value={updateData.unit} onValueChange={(value) => setUpdateData({ ...updateData, unit: value })}>
                      <SelectTrigger className="bg-white text-stone-900 border-stone-300"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNIT_OPTIONS.map((unit) => (<SelectItem key={unit} value={unit}>{unit}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-stone-700 text-sm">Vendor Rate (₹)</Label>
                    <Input type="number" min="0" step="0.01" className="bg-white text-stone-900 border-stone-300"
                      value={updateData._vendorRate ?? updateData.vendor_rate ?? updateData.rate ?? 0}
                      onChange={(e) => {
                        const vr = parseFloat(e.target.value) || 0;
                        setUpdateData({ ...updateData, rate: vr, vendor_rate: vr, _vendorRate: vr });
                      }}
                    />
                  </div>
                </div>
                {updateData.quantity > 0 && (updateData._vendorRate ?? updateData.vendor_rate ?? updateData.rate) > 0 && (() => {
                  const vr = updateData._vendorRate ?? updateData.vendor_rate ?? updateData.rate ?? 0;
                  const adminMk = updateData.admin_markup_percentage ?? (projects.find(p => p.id === selectedTask?.project_id)?.admin_markup_percentage ?? projects.find(p => p.id === selectedTask?.project_id)?.vendor_markdown_percentage ?? 20);
                  const ar = Math.round(vr * (1 + adminMk / 100) * 100) / 100;
                  return (
                  <div className="mt-3 text-sm flex gap-4">
                    <span className="text-stone-600">Vendor Amount: <span className="font-bold text-purple-700">₹{(updateData.quantity * vr).toLocaleString()}</span></span>
                    <span className="text-stone-400">Admin Amount: ₹{(updateData.quantity * ar).toLocaleString()}</span>
                  </div>
                  );
                })()}
              </div>

              {/* AI Cost Estimation for Edit Task */}
              {(updateData.title || updateData.description) && (
                <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-stone-900 flex items-center gap-2">
                      <Sparkles size={14} className="text-purple-500" />
                      AI Cost Estimation
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateAICostEstimate}
                      disabled={aiCostEstimateLoading || (!updateData.title && !updateData.description)}
                      className="text-purple-600 border-purple-300 hover:bg-purple-100 h-7 px-3 text-xs"
                      data-testid="ai-estimate-cost-edit-btn"
                    >
                      {aiCostEstimateLoading ? (
                        <span className="flex items-center gap-1"><span className="animate-spin">⏳</span> Estimating...</span>
                      ) : (
                        <span className="flex items-center gap-1"><Wand2 size={14} /> Estimate Cost</span>
                      )}
                    </Button>
                  </div>
                  {aiCostEstimate && (
                    <div className="mt-2 p-2 bg-white rounded border border-purple-100">
                      {aiCostEstimate.min_rate > 0 && aiCostEstimate.max_rate > 0 ? (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-stone-600">Estimated Range:</span>
                            <span className="font-medium text-purple-700">₹{aiCostEstimate.min_rate?.toLocaleString()} - ₹{aiCostEstimate.max_rate?.toLocaleString()} per {updateData.unit || 'unit'}</span>
                          </div>
                          {aiCostEstimate.suggested_rate > 0 && (
                            <div className="flex items-center justify-between text-sm mt-1">
                              <span className="text-stone-600">Suggested Rate:</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-green-600 font-bold hover:bg-green-50 h-6 px-2"
                                onClick={() => setUpdateData({ ...updateData, rate: aiCostEstimate.suggested_rate, vendor_rate: aiCostEstimate.suggested_rate, _vendorRate: aiCostEstimate.suggested_rate })}
                              >
                                ₹{aiCostEstimate.suggested_rate?.toLocaleString()} (Apply)
                              </Button>
                            </div>
                          )}
                          {aiCostEstimate.confidence && (
                            <div className="text-xs text-stone-500 mt-1">
                              Confidence: <span className={aiCostEstimate.confidence === 'high' ? 'text-green-600' : aiCostEstimate.confidence === 'medium' ? 'text-amber-600' : 'text-stone-500'}>{aiCostEstimate.confidence}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-stone-500">{aiCostEstimate.notes || 'Unable to estimate. Please enter rate manually.'}</p>
                      )}
                    </div>
                  )}
                  {!aiCostEstimate && (
                    <p className="text-xs text-stone-500">Click "Estimate Cost" to get AI-powered rate suggestions.</p>
                  )}
                </div>
              )}

              {/* Per-Task Markup Overrides — Dynamic based on project pricing model */}
              {(user.role === 'owner' || user.role === 'staff') && (updateData._vendorRate ?? updateData.vendor_rate ?? updateData.rate) > 0 && (() => {
                const proj = projects.find(p => p.id === selectedTask?.project_id);
                const isClientToVendor = proj?.pricing_model === 'client_to_vendor';
                return (
                <div className="border border-stone-200 rounded-lg p-4 bg-stone-50/50">
                  <h4 className="font-semibold text-stone-700 mb-1 flex items-center gap-2 text-sm">
                    Rate Layer Overrides ({isClientToVendor ? 'Client → Admin → Vendor' : 'Vendor → Admin → Client'})
                  </h4>
                  <p className="text-xs text-stone-400 mb-3">
                    {isClientToVendor 
                      ? 'Client-to-Vendor model: Client rate is the base. Admin Markdown and Vendor Markdown are applied downward.'
                      : 'Override project-level defaults for this task. Leave blank to use project defaults.'}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-700 text-sm">Admin Markup %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="200"
                        step="0.1"
                        placeholder="Project default"
                        className="bg-white text-stone-900 border-stone-300"
                        value={updateData.admin_markup_percentage ?? ''}
                        onChange={(e) => setUpdateData({ ...updateData, admin_markup_percentage: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })}
                        data-testid="task-admin-markup"
                      />
                      <p className="text-xs text-stone-400 mt-0.5">Admin Rate = Vendor Rate × (1 + markup%)</p>
                    </div>
                    <div>
                      <Label className="text-stone-700 text-sm">Client Markup %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="200"
                        step="0.1"
                        placeholder="Project default"
                        className="bg-white text-stone-900 border-stone-300"
                        value={updateData.client_markup_percentage ?? ''}
                        onChange={(e) => setUpdateData({ ...updateData, client_markup_percentage: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })}
                        data-testid="task-client-markup"
                      />
                      <p className="text-xs text-stone-400 mt-0.5">Client Rate = Admin Rate × (1 + markup%)</p>
                    </div>
                  </div>
                  {(updateData._vendorRate ?? updateData.vendor_rate ?? updateData.rate) > 0 && (() => {
                    const vr = updateData._vendorRate ?? updateData.vendor_rate ?? updateData.rate ?? 0;
                    const adminMk = updateData.admin_markup_percentage ?? (projects.find(p => p.id === selectedTask?.project_id)?.admin_markup_percentage ?? projects.find(p => p.id === selectedTask?.project_id)?.vendor_markdown_percentage ?? 20);
                    const clientMk = updateData.client_markup_percentage ?? (projects.find(p => p.id === selectedTask?.project_id)?.client_markup_percentage ?? projects.find(p => p.id === selectedTask?.project_id)?.markup_percentage ?? 20);
                    const ar = Math.round(vr * (1 + adminMk / 100) * 100) / 100;
                    const cr = Math.round(ar * (1 + clientMk / 100) * 100) / 100;
                    const qty = updateData.quantity || 1;
                    const vAmt = Math.round(vr * qty * 100) / 100;
                    const aAmt = Math.round(ar * qty * 100) / 100;
                    const cAmt = Math.round(cr * qty * 100) / 100;
                    return (
                    <div className="mt-3 p-2 bg-white rounded border border-stone-200 grid grid-cols-3 gap-2 text-center text-xs">
                      <div>
                        <span className="text-purple-600 block">Vendor Rate (Input)</span>
                        <span className="font-semibold text-purple-700">₹{vr.toLocaleString()}</span>
                        <span className="block text-[10px] text-purple-500 mt-0.5">Amt: ₹{vAmt.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-stone-600 block">Admin Rate (+{adminMk}%)</span>
                        <span className="font-semibold text-stone-700">₹{ar.toLocaleString()}</span>
                        <span className="block text-[10px] text-stone-500 mt-0.5">Amt: ₹{aAmt.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-green-600 block">Client Rate (+{clientMk}%)</span>
                        <span className="font-semibold text-green-700">₹{cr.toLocaleString()}</span>
                        <span className="block text-[10px] text-green-500 mt-0.5">Amt: ₹{cAmt.toLocaleString()}</span>
                      </div>
                    </div>
                    );
                  })()}
                </div>
                );
              })()}

              {/* Actual Values Section - shown when task is in_progress or completed */}
              {(updateData.status === 'in_progress' || updateData.status === 'completed') && (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50/50">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    Actual (Site Completion)
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-stone-700 text-sm">Actual Quantity</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        step="0.1" 
                        className="bg-white text-stone-900 border-stone-300" 
                        value={updateData.actual_quantity ?? updateData.quantity ?? 0} 
                        onChange={(e) => setUpdateData({ ...updateData, actual_quantity: parseFloat(e.target.value) || 0 })} 
                      />
                    </div>
                    <div>
                      <Label className="text-stone-700 text-sm">Unit</Label>
                      <Input 
                        disabled 
                        className="bg-stone-100 text-stone-600 border-stone-300" 
                        value={updateData.unit} 
                      />
                    </div>
                    <div>
                      <Label className="text-stone-700 text-sm">Actual Rate (₹)</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        step="0.1" 
                        className="bg-white text-stone-900 border-stone-300"
                        value={updateData.actual_rate ?? updateData.rate ?? 0} 
                        onChange={(e) => setUpdateData({ ...updateData, actual_rate: parseFloat(e.target.value) || 0 })} 
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-stone-700 text-sm">Remarks (Variance Reason)</Label>
                    <Input 
                      className="bg-white text-stone-900 border-stone-300 mt-1" 
                      placeholder="e.g., Site measurement differed from design"
                      value={updateData.actual_remarks || ''} 
                      onChange={(e) => setUpdateData({ ...updateData, actual_remarks: e.target.value })} 
                    />
                  </div>
                  {/* Show actual amount calculation */}
                  {(() => {
                    const actualQty = updateData.actual_quantity ?? updateData.quantity;
                    const actualRate = updateData.actual_rate ?? updateData.rate;
                    const estimatedAmount = (updateData.quantity || 0) * (updateData.rate || 0);
                    const actualAmount = (actualQty || 0) * (actualRate || 0);
                    const variance = actualAmount - estimatedAmount;
                    const variancePercent = estimatedAmount > 0 ? ((variance / estimatedAmount) * 100).toFixed(1) : 0;
                    
                    return (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-600">Actual Amount:</span>
                          <span className="font-bold text-green-700">₹{actualAmount.toLocaleString()}</span>
                        </div>
                        {variance !== 0 && (
                          <div className="flex justify-between text-sm mt-1">
                            <span className="text-stone-600">Variance:</span>
                            <span className={`font-medium ${variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {variance > 0 ? '+' : ''}₹{variance.toLocaleString()} ({variance > 0 ? '+' : ''}{variancePercent}%)
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Client Approval Request (NEW) */}
              {(user.role === 'owner' || user.role === 'staff') && (
                <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/50">
                  <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <UserCheck size={16} />
                    Client Approval
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="requires_approval"
                        checked={updateData.requires_client_approval || false}
                        onCheckedChange={(checked) => setUpdateData({ ...updateData, requires_client_approval: checked })}
                        className="border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                      />
                      <label htmlFor="requires_approval" className="text-sm text-amber-800 cursor-pointer">
                        Require client approval for this task
                      </label>
                    </div>
                    {updateData.requires_client_approval && (
                      <div>
                        <Label className="text-amber-800 text-sm">Message to Client (optional)</Label>
                        <Input
                          placeholder="e.g., Please review and approve the design layout"
                          className="bg-white border-amber-300 mt-1"
                          value={updateData.approval_message || ''}
                          onChange={(e) => setUpdateData({ ...updateData, approval_message: e.target.value })}
                        />
                      </div>
                    )}
                    {selectedTask?.client_approval_status && (
                      <div className={`text-sm p-2 rounded ${
                        selectedTask.client_approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                        selectedTask.client_approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        Status: {selectedTask.client_approval_status === 'approved' ? '✓ Approved' : 
                                 selectedTask.client_approval_status === 'rejected' ? '✗ Rejected' : '⏳ Pending'}
                        {selectedTask.client_approval_comment && (
                          <span className="block mt-1 text-xs">{selectedTask.client_approval_comment}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setUpdateDialogOpen(false)} className="text-stone-900">Cancel</Button>
                <Button type="submit" className="apple-button">Save Changes</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Task Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-stone-900">
                <AlertCircle className="text-red-500" size={20} />
                Delete Task
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong className="text-stone-900">&quot;{selectedTask?.title}&quot;</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="text-stone-900">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete Task</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Delete Dialog */}
        <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-stone-900">
                <AlertCircle className="text-red-500" size={20} />
                Delete {selectedTasks.size} Task(s)
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong className="text-stone-900">{selectedTasks.size} tasks</strong>? Tasks that are in BOQ worksheets will be skipped. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="text-stone-900">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete Tasks</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Categorize Dialog */}
        <Dialog open={bulkCategoryDialogOpen} onOpenChange={setBulkCategoryDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900">
                Categorize {selectedTasks.size} Task(s)
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-stone-500 text-sm">Assign a category to all selected tasks at once.</p>
              
              <div>
                <Label className="text-stone-900">Main Category</Label>
                <Select 
                  value={bulkCategoryData.category_group} 
                  onValueChange={(value) => setBulkCategoryData({ category_group: value, category_code: '' })}
                >
                  <SelectTrigger className="w-full bg-white text-stone-900 border-stone-300 mt-1">
                    <SelectValue placeholder="Select main category..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {categories.map(group => (
                      <SelectItem key={group.group} value={group.group}>
                        {group.group} - {group.group_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {bulkCategoryData.category_group && (
                <div>
                  <Label className="text-stone-900">Subcategory (Optional)</Label>
                  <Select 
                    value={bulkCategoryData.category_code} 
                    onValueChange={(value) => setBulkCategoryData({ ...bulkCategoryData, category_code: value })}
                  >
                    <SelectTrigger className="w-full bg-white text-stone-900 border-stone-300 mt-1">
                      <SelectValue placeholder="Select subcategory..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      <SelectItem value="none">No subcategory</SelectItem>
                      {(categories.find(g => g.group === bulkCategoryData.category_group)?.categories || []).map(cat => (
                        <SelectItem key={cat.code} value={cat.code}>
                          {cat.code} - {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setBulkCategoryDialogOpen(false)} className="text-stone-900">Cancel</Button>
                <Button onClick={handleBulkCategorize} className="apple-button">Apply to {selectedTasks.size} Tasks</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Mode Toggle (Admin Only) - Merged Tasks & Schedule */}
        {(user.role === 'owner' || user.role === 'staff') && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-stone-500">View:</span>
            <div className="inline-flex rounded-lg bg-stone-100 p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 text-sm rounded-md transition-all flex items-center gap-2 ${viewMode === 'list' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                data-testid="view-mode-list"
              >
                <ListTodo size={16} />
                Task List
              </button>
              <button
                onClick={() => setViewMode('schedule')}
                className={`px-4 py-2 text-sm rounded-md transition-all flex items-center gap-2 ${viewMode === 'schedule' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                data-testid="view-mode-schedule"
              >
                <Calendar size={16} />
                Timeline View
              </button>
            </div>
          </div>
        )}

        {/* Filters & Tabs */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <Tabs value={viewTab} onValueChange={setViewTab} className="w-full md:w-auto">
            <TabsList className="bg-stone-100">
              <TabsTrigger value="all" className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
                <ListTodo size={16} />
                All ({filteredTasks.length})
              </TabsTrigger>
              <TabsTrigger value="pending_approval" className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
                <AlertCircle size={16} />
                Pending ({taskCounts.pending_approval})
              </TabsTrigger>
              <TabsTrigger value="approved" className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
                <CheckCircle2 size={16} />
                Approved
              </TabsTrigger>
              <TabsTrigger value="activities" className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900" data-testid="activities-tab">
                <ClipboardList size={16} />
                Activities ({filteredActivities.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex gap-3">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-56 bg-white text-stone-800 border-stone-300">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px] overflow-y-auto">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((group) => (
                  <div key={group.group}>
                    {/* Group Header */}
                    <div className="px-2 py-2 text-xs font-bold text-stone-600 uppercase tracking-wide bg-stone-100 sticky top-0">
                      {group.group}. {group.group_name}
                    </div>
                    {/* Subcategories */}
                    {(group.categories || []).map((cat) => (
                      <SelectItem key={cat.code} value={cat.code} className="pl-4 text-stone-700">
                        {cat.code} - {cat.name}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-full md:w-64 bg-white text-stone-800 border-stone-300">
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {[...projects]
                  .filter(p => {
                    // iter-338z8: reflect the Active/Inactive filter here too
                                        return matchesProjectFilter(p, projectActiveFilter);
                  })
                  .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                  .map((p, idx) => <SelectItem key={p.id} value={p.id}>{idx + 1}. {p.name}{p.is_active === false ? ' · Inactive' : ''}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* iter-338z8: Active / Inactive / All project filter — matches the Projects page */}
            <div className="inline-flex items-center rounded-md border border-stone-300 overflow-hidden text-xs" data-testid="tasks-project-active-filter">
              {[
                { v: 'active', label: 'Active' },
                { v: 'inactive', label: 'Inactive' },
                { v: 'all', label: 'All' },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => { setProjectActiveFilter(opt.v); if (selectedProject !== 'all') setSelectedProject('all'); }}
                  className={`px-3 py-1.5 font-medium transition-colors ${projectActiveFilter === opt.v ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                  data-testid={`tasks-project-active-filter-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2 bg-white">
                  <ArrowUpDown size={16} />
                  Sort: {sortBy === 'custom' ? 'Custom Order' : 
                         sortBy === 'start_date' ? 'Start Date' :
                         sortBy === 'deadline' ? 'Deadline' :
                         sortBy === 'category' ? 'Category' : 'Status'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy('custom')} className={sortBy === 'custom' ? 'bg-stone-100' : ''}>
                  <Check size={14} className={`mr-2 ${sortBy === 'custom' ? 'opacity-100' : 'opacity-0'}`} />
                  Custom Order (Sr.No)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('start_date')} className={sortBy === 'start_date' ? 'bg-stone-100' : ''}>
                  <Check size={14} className={`mr-2 ${sortBy === 'start_date' ? 'opacity-100' : 'opacity-0'}`} />
                  By Start Date
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('deadline')} className={sortBy === 'deadline' ? 'bg-stone-100' : ''}>
                  <Check size={14} className={`mr-2 ${sortBy === 'deadline' ? 'opacity-100' : 'opacity-0'}`} />
                  By Deadline
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('category')} className={sortBy === 'category' ? 'bg-stone-100' : ''}>
                  <Check size={14} className={`mr-2 ${sortBy === 'category' ? 'opacity-100' : 'opacity-0'}`} />
                  By Category
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('status')} className={sortBy === 'status' ? 'bg-stone-100' : ''}>
                  <Check size={14} className={`mr-2 ${sortBy === 'status' ? 'opacity-100' : 'opacity-0'}`} />
                  By Status
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Reorder Tasks Button */}
            <Button 
              variant={showReorderMode ? "default" : "outline"}
              onClick={() => {
                if (showReorderMode) {
                  setShowReorderMode(false);
                } else {
                  setShowReorderMode(true);
                  setSortBy('custom'); // Switch to custom order when reordering
                }
              }}
              className={`flex items-center gap-2 ${showReorderMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white'}`}
              data-testid="reorder-tasks-btn"
            >
              <GripVertical size={16} />
              {showReorderMode ? 'Exit Reorder' : 'Reorder Tasks'}
            </Button>
          </div>
        </div>
        
        {/* Reorder Mode Banner */}
        {showReorderMode && (
          <div className="apple-card bg-blue-50 border-blue-200 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GripVertical size={20} className="text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Reorder Mode Active</p>
                <p className="text-sm text-blue-700">Drag tasks to reorder. Sr.No will update automatically.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedProject !== 'all' && (
                <Button 
                  onClick={() => saveTaskOrder(selectedProject)}
                  disabled={savingOrder}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  data-testid="save-order-btn"
                >
                  {savingOrder ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Order
                    </>
                  )}
                </Button>
              )}
              {selectedProject === 'all' && (
                <p className="text-sm text-blue-600">Select a project to save custom order</p>
              )}
              <Button 
                variant="outline"
                onClick={() => setShowReorderMode(false)}
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Summary Cards - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setStatusFilter('all')}
            data-testid="summary-total"
          >
            <div className="flex items-center gap-3 mb-2">
              <ListTodo size={20} className="text-stone-800" />
              <span className="text-2xl font-bold">{filteredTasks.length}</span>
            </div>
            <p className="text-sm text-stone-500">Total Tasks</p>
          </div>
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'not_started' ? 'ring-2 ring-stone-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'not_started' ? 'all' : 'not_started')}
            data-testid="summary-not-started"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock size={20} className="text-stone-500" />
              <span className="text-2xl font-bold">{taskCounts.not_started}</span>
            </div>
            <p className="text-sm text-stone-500">Not Started</p>
          </div>
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'in_progress' ? 'ring-2 ring-blue-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'in_progress' ? 'all' : 'in_progress')}
            data-testid="summary-in-progress"
          >
            <div className="flex items-center gap-3 mb-2">
              <PlayCircle size={20} className="text-blue-500" />
              <span className="text-2xl font-bold">{taskCounts.in_progress}</span>
            </div>
            <p className="text-sm text-stone-500">In Progress</p>
          </div>
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'completed' ? 'ring-2 ring-green-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')}
            data-testid="summary-completed"
          >
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 size={20} className="text-green-500" />
              <span className="text-2xl font-bold">{taskCounts.completed}</span>
            </div>
            <p className="text-sm text-stone-500">Completed</p>
          </div>
          <div 
            className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'delayed' ? 'ring-2 ring-red-400' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'delayed' ? 'all' : 'delayed')}
            data-testid="summary-delayed"
          >
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle size={20} className="text-red-500" />
              <span className="text-2xl font-bold">{taskCounts.delayed}</span>
            </div>
            <p className="text-sm text-stone-500">Delayed</p>
          </div>
        </div>

        {/* Tasks List - Only show in 'list' viewMode and non-activities tab */}
        {viewMode === 'list' && viewTab !== 'activities' && (
          <>
            {filteredTasks.length === 0 ? (
              <div className="apple-card p-16 text-center">
                <ListTodo size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500">No tasks found</p>
                <p className="text-sm text-stone-400 mt-1">Try adjusting your filters or create a new task</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="tasks-list">
                {/* Select All Header & Expand/Collapse Controls */}
                <div className="apple-card px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                  checked={selectedTasks.size === filteredTasks.length && filteredTasks.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedTasks(new Set(filteredTasks.map(t => t.id)));
                    } else {
                      setSelectedTasks(new Set());
                    }
                  }}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm font-medium text-stone-600">
                  {selectedTasks.size > 0 
                    ? `${selectedTasks.size} of ${filteredTasks.length} selected`
                    : `Select all (${filteredTasks.length} tasks)`
                  }
                </span>
              </div>
              <div className="flex items-center gap-3">
                {selectedTasks.size > 0 && (
                  <button
                    onClick={() => setSelectedTasks(new Set())}
                    className="text-sm text-stone-500 hover:text-stone-700"
                  >
                    Clear selection
                  </button>
                )}
                <div className="flex gap-1 border-l pl-3">
                  <button
                    onClick={expandAllProjects}
                    className="text-xs px-2 py-1 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded"
                    data-testid="expand-all-btn"
                  >
                    Expand All
                  </button>
                  <button
                    onClick={collapseAllProjects}
                    className="text-xs px-2 py-1 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded"
                    data-testid="collapse-all-btn"
                  >
                    Collapse All
                  </button>
                </div>
              </div>
            </div>

            {/* Collapsible Project Groups */}
            {Object.keys(groupedByProject)
              .sort((a, b) => {
                const projA = projects.find(p => p.id === a);
                const projB = projects.find(p => p.id === b);
                const dateA = projA?.created_at || '';
                const dateB = projB?.created_at || '';
                return dateB.localeCompare(dateA); // newest first
              })
              .map((projectId) => {
              const projectTasks = groupedByProject[projectId];
              const isExpanded = expandedProjects.has(projectId);
              const completedCount = projectTasks.filter(t => t.status === 'completed').length;
              const totalAmount = projectTasks.reduce((sum, t) => sum + (t.task_cost || 0), 0);
              const selectedInProject = projectTasks.filter(t => selectedTasks.has(t.id)).length;
              const allProjectTasksSelected = selectedInProject === projectTasks.length;
              
              // Toggle all tasks in this project
              const toggleProjectTasks = (e) => {
                e.stopPropagation();
                setSelectedTasks(prev => {
                  const next = new Set(prev);
                  if (allProjectTasksSelected) {
                    // Deselect all in this project
                    projectTasks.forEach(t => next.delete(t.id));
                  } else {
                    // Select all in this project
                    projectTasks.forEach(t => next.add(t.id));
                  }
                  return next;
                });
              };
              
              return (
                <Collapsible 
                  key={projectId} 
                  open={isExpanded} 
                  onOpenChange={() => toggleProjectExpansion(projectId)}
                  className="apple-card overflow-hidden"
                >
                  <CollapsibleTrigger asChild>
                    <div className="bg-white px-6 py-4 border-b border-stone-100 cursor-pointer hover:bg-stone-50 transition-colors flex items-center justify-between" data-testid={`project-header-${projectId}`}>
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown size={20} className="text-stone-400" /> : <ChevronRight size={20} className="text-stone-400" />}
                        <div>
                          <h3 className="font-semibold text-stone-800">{getProjectName(projectId)}</h3>
                          <p className="text-xs text-stone-500 mt-0.5">
                            {projectTasks.length} tasks • {completedCount} completed • ₹{totalAmount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Select All in Project */}
                        <button
                          onClick={toggleProjectTasks}
                          className="flex items-center gap-2 px-3 py-1 text-xs rounded-full hover:bg-stone-100 transition-colors text-stone-600"
                          data-testid={`select-all-project-${projectId}`}
                        >
                          {allProjectTasksSelected ? (
                            <CheckSquare size={14} className="text-stone-800" />
                          ) : selectedInProject > 0 ? (
                            <Square size={14} className="text-stone-400" />
                          ) : (
                            <Square size={14} className="text-stone-300" />
                          )}
                          <span>{selectedInProject > 0 ? `${selectedInProject}/${projectTasks.length}` : `Select ${projectTasks.length}`}</span>
                        </button>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${completedCount === projectTasks.length && projectTasks.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>
                          {projectTasks.length > 0 ? Math.round((completedCount / projectTasks.length) * 100) : 0}%
                        </span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="divide-y divide-stone-100">
                      {projectTasks.map((task, taskIndex) => {
                        // Render header rows as amber section dividers
                        if (task.item_type === 'header') {
                          return (
                            <div key={task.id} className="px-4 md:px-6 py-3 bg-amber-50 border-y-2 border-amber-300 flex items-center justify-between" data-testid={`task-header-${task.id}`}>
                              <div className="flex items-center gap-2">
                                <LayoutDashboard size={16} className="text-amber-700" />
                                <span className="font-bold text-base text-amber-900">{task.title}</span>
                              </div>
                              <button onClick={async () => {
                                if (!confirm(`Delete header "${task.title}"?`)) return;
                                try {
                                  await axios.delete(`${API}/tasks/${task.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                                  fetchTasks();
                                } catch { toast.error('Failed to delete header'); }
                              }} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          );
                        }
                        
                        const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.not_started;
                        const StatusIcon = statusConfig.icon;
                        const isSelected = selectedTasks.has(task.id);
                        const canApprove = (user.role === 'owner' || user.role === 'staff') && task.task_cost && !task.approved;
                        const serialNumber = taskIndex + 1;
                        const isDragging = draggedTaskId === task.id;
                        
                        return (
                          <div 
                            key={task.id} 
                            className={`p-4 md:p-6 group hover:bg-stone-50 transition-colors ${
                              task.imported_from_vendor 
                                ? 'bg-amber-50 border-l-4 border-l-amber-500' 
                                : isSelected ? 'bg-primary/5' : ''
                            } ${isDragging ? 'opacity-50 bg-blue-50' : ''} ${showReorderMode ? 'cursor-grab active:cursor-grabbing' : ''}`} 
                            data-testid={`task-${task.id}`}
                            draggable={showReorderMode}
                            onDragStart={(e) => handleDragStart(e, task.id)}
                            onDragOver={(e) => handleDragOver(e, task.id)}
                            onDrop={(e) => handleDrop(e, task.id, projectId)}
                            onDragEnd={() => setDraggedTaskId(null)}
                          >
                            <div className="flex gap-4">
                              {/* Drag Handle & Sr.No */}
                              <div className="flex items-center gap-2">
                                {showReorderMode && (
                                  <div className="cursor-grab text-stone-400 hover:text-stone-600" data-drag-grip="true"
                                    onTouchStart={(e) => {
                                      e.preventDefault();
                                      const touch = e.touches[0];
                                      window._touchDragTask = { id: task.id, startY: touch.clientY };
                                    }}
                                    onTouchMove={(e) => {
                                      if (!window._touchDragTask) return;
                                      e.preventDefault();
                                      const touch = e.touches[0];
                                      const el = document.elementFromPoint(touch.clientX, touch.clientY);
                                      const target = el?.closest('[data-testid^="task-"]');
                                      if (target) {
                                        const tid = target.getAttribute('data-testid').replace('task-', '');
                                        window._touchDragTarget = tid;
                                      }
                                    }}
                                    onTouchEnd={() => {
                                      if (window._touchDragTask && window._touchDragTarget) {
                                        // Trigger reorder
                                        const fromId = window._touchDragTask.id;
                                        const toId = window._touchDragTarget;
                                        if (fromId !== toId) {
                                          handleDrop({ preventDefault: () => {} }, toId, projectId, fromId);
                                        }
                                      }
                                      window._touchDragTask = null;
                                      window._touchDragTarget = null;
                                    }}
                                  >
                                    <GripVertical size={18} />
                                  </div>
                                )}
                                <div className="flex items-center justify-center w-8 h-8 bg-stone-100 rounded-full text-sm font-semibold text-stone-600" title={`Serial Number ${serialNumber}`}>
                                  {serialNumber}
                                </div>
                              </div>
                              
                              {/* Checkbox */}
                              <div className="flex items-start pt-1">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleTaskSelection(task.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`task-checkbox-${task.id}`}
                                />
                              </div>
                              
                              {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h4 className="font-semibold text-stone-900">{task.title}</h4>
                              {/* Status Badge */}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                                <StatusIcon size={12} />
                                {statusConfig.label}
                              </span>
                              {task.approved && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  Approved
                                </span>
                              )}
                              {task.imported_from_vendor && (
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded" data-testid={`vendor-tag-list-${task.id}`}>
                                  {task.vendor_quote_tag || 'Vendor Quote'}
                                </span>
                              )}
                              {task.category_code && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded" title={task.category_name}>
                                  {task.category_code}
                                </span>
                              )}
                            </div>
                            
                            {/* Collapsible Task Description - Default Collapsed for Space Optimization */}
                            {task.description && (
                              <div className="mb-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleDescription(task.id); }}
                                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium mb-1"
                                  data-testid={`toggle-desc-${task.id}`}
                                >
                                  <ChevronDown 
                                    size={14} 
                                    className={`transition-transform ${expandedDescriptions.has(task.id) ? 'rotate-180' : ''}`} 
                                  />
                                  {expandedDescriptions.has(task.id) ? 'Hide Description' : 'Show Description'}
                                </button>
                                {expandedDescriptions.has(task.id) && (
                                  <div 
                                    className="text-sm text-stone-600 leading-relaxed prose prose-sm max-w-none pl-4 border-l-2 border-purple-200 bg-stone-50 p-3 rounded-r-lg"
                                    dangerouslySetInnerHTML={{ __html: formatDescriptionHTML(task.description) }}
                                  />
                                )}
                              </div>
                            )}
                            
                            <div className="flex flex-wrap gap-3 text-xs text-stone-500 mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {formatDate(task.deadline)}
                              </span>
                              {task.quantity && task.unit && (
                                <span>Qty: {task.quantity} {task.unit}</span>
                              )}
                              {task.rate > 0 && (
                                <span>Rate: ₹{task.rate.toLocaleString()}</span>
                              )}
                              {task.task_cost > 0 && (
                                <span className="font-semibold text-stone-900">Total: ₹{task.task_cost.toLocaleString()}</span>
                              )}
                            </div>
                            
                            {/* Progress Slider - Interactive for Admin/Vendor */}
                            <div className="max-w-xs" onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                              <ProgressSlider
                                progress={task.progress || 0}
                                onProgressChange={(val) => handleProgressChange(task, val)}
                                disabled={false}
                                size="sm"
                                showLabels={true}
                              />
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-2 items-start">
                            {canApprove && (
                              <button
                                onClick={(e) => handleApprove(task.id, e)}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded-full hover:bg-green-700 transition-colors flex items-center gap-1"
                                data-testid={`approve-task-${task.id}`}
                              >
                                <Check size={12} />
                                Approve
                              </button>
                            )}
                            <button
                              onClick={(e) => openAssignVendorDialog(task, e)}
                              className={`p-2 ${task.assigned_to ? 'text-green-500' : 'text-amber-500'} hover:text-amber-600 hover:bg-amber-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity`}
                              title={task.assigned_to ? 'Reassign Vendor' : 'Assign Vendor'}
                              data-testid={`assign-vendor-${task.id}`}
                            >
                              <UserPlus size={16} />
                            </button>
                            {/* P0: Client Approval Button in Timeline */}
                            <button
                              onClick={(e) => openClientApprovalDialog(task, e)}
                              className={`p-2 ${task.client_approval_status === 'pending' ? 'text-orange-500' : task.client_approval_status === 'approved' ? 'text-green-500' : 'text-purple-400'} hover:text-purple-600 hover:bg-purple-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity`}
                              title={task.client_approval_status === 'pending' ? 'Awaiting Client Approval' : task.client_approval_status === 'approved' ? 'Client Approved' : 'Require Client Approval'}
                              data-testid={`client-approval-timeline-${task.id}`}
                            >
                              <ShieldCheck size={16} />
                            </button>
                            <button
                              onClick={(e) => openEditDialog(task, e)}
                              className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={(e) => openDeleteDialog(task, e)}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        )}
          </>
        )}

        {/* Activities View - P1: Merged from Schedule.js */}
        {viewMode === 'list' && viewTab === 'activities' && (
          <div className="space-y-4" data-testid="activities-list">
            {filteredActivities.length === 0 ? (
              <div className="apple-card p-16 text-center">
                <ClipboardList size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500">No activities found</p>
                <p className="text-sm text-stone-400 mt-1">Activities track work logs and progress updates from vendors</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Group activities by project */}
                {Object.entries(
                  filteredActivities.reduce((acc, activity) => {
                    const projectId = activity.project_id || 'no_project';
                    if (!acc[projectId]) acc[projectId] = [];
                    acc[projectId].push(activity);
                    return acc;
                  }, {})
                ).map(([projectId, projectActivities]) => {
                  const projectName = projects.find(p => p.id === projectId)?.name || 'No Project';
                  return (
                    <div key={projectId} className="apple-card overflow-hidden">
                      <div className="px-4 py-3 bg-stone-50 border-b flex items-center gap-3">
                        <ClipboardList size={18} className="text-purple-500" />
                        <h3 className="font-semibold text-stone-900">{projectName}</h3>
                        <span className="text-sm text-stone-400">({projectActivities.length} activities)</span>
                      </div>
                      <div className="divide-y">
                        {projectActivities.map(activity => (
                          <div key={activity.id} className="p-4 hover:bg-stone-50 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium text-stone-900">{activity.name}</h4>
                                {activity.description && (
                                  <p className="text-sm text-stone-500 mt-1">{activity.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                                  <span className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    {formatDate(activity.start_date)} - {formatDate(activity.end_date)}
                                  </span>
                                  {activity.assigned_to && (
                                    <span className="flex items-center gap-1">
                                      <Users size={12} />
                                      {users.find(u => u.id === activity.assigned_to)?.name || 'Unassigned'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  activity.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  activity.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  activity.status === 'delayed' ? 'bg-red-100 text-red-700' :
                                  'bg-stone-100 text-stone-600'
                                }`}>
                                  {activity.status?.replace('_', ' ') || 'Not Started'}
                                </span>
                                {activity.progress !== undefined && (
                                  <span className="text-sm font-medium text-stone-600">{activity.progress || 0}%</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Schedule Timeline View (Admin Only) */}
        {viewMode === 'schedule' && (user.role === 'owner' || user.role === 'staff') && (
          <div className="space-y-4">
            {/* Timeline Header */}
            <div className="apple-card px-6 py-4 bg-gradient-to-r from-stone-50 to-stone-100/50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-stone-900">Task Timeline</h3>
                  <p className="text-sm text-stone-500">{filteredTasks.length} tasks across {Object.keys(groupedByProject).length} projects</p>
                </div>
                <div className="flex gap-2">
                  {['not_started', 'in_progress', 'completed', 'delayed'].map(status => (
                    <div key={status} className="flex items-center gap-1 text-xs">
                      <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG[status]?.color.split(' ')[0]}`}></div>
                      <span className="text-stone-500">{STATUS_CONFIG[status]?.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Timeline by Week */}
            {(() => {
              // Group tasks by week
              const tasksByWeek = {};
              const today = new Date();
              
              filteredTasks.forEach(task => {
                const deadline = task.deadline ? new Date(task.deadline) : null;
                if (!deadline) {
                  if (!tasksByWeek['no_deadline']) tasksByWeek['no_deadline'] = [];
                  tasksByWeek['no_deadline'].push(task);
                  return;
                }
                
                const weekStart = new Date(deadline);
                weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                const weekKey = weekStart.toISOString().split('T')[0];
                
                if (!tasksByWeek[weekKey]) tasksByWeek[weekKey] = [];
                tasksByWeek[weekKey].push(task);
              });
              
              // Sort weeks
              const sortedWeeks = Object.keys(tasksByWeek)
                .filter(k => k !== 'no_deadline')
                .sort((a, b) => new Date(a) - new Date(b));
              
              if (tasksByWeek['no_deadline']) {
                sortedWeeks.push('no_deadline');
              }
              
              return sortedWeeks.map(weekKey => {
                const weekTasks = tasksByWeek[weekKey];
                const isNoDeadline = weekKey === 'no_deadline';
                const weekDate = isNoDeadline ? null : new Date(weekKey);
                const weekEnd = isNoDeadline ? null : new Date(weekDate.getTime() + 6 * 24 * 60 * 60 * 1000);
                const isPast = weekEnd && weekEnd < today;
                const isCurrent = weekDate && weekDate <= today && weekEnd >= today;
                
                return (
                  <div key={weekKey} className={`apple-card overflow-hidden ${isCurrent ? 'ring-2 ring-blue-400' : ''}`}>
                    <div className={`px-6 py-3 flex items-center justify-between ${isPast ? 'bg-stone-100' : isCurrent ? 'bg-blue-50' : 'bg-stone-50'}`}>
                      <div className="flex items-center gap-3">
                        <Calendar size={18} className={isPast ? 'text-stone-400' : isCurrent ? 'text-blue-600' : 'text-stone-600'} />
                        <div>
                          <p className={`font-medium ${isPast ? 'text-stone-500' : 'text-stone-900'}`}>
                            {isNoDeadline ? 'No Deadline Set' : `Week of ${formatDate(weekDate, { shortMonth: true })} - ${formatDate(weekEnd, { shortMonth: true })}`}
                          </p>
                          <p className="text-xs text-stone-500">{weekTasks.length} tasks</p>
                        </div>
                      </div>
                      {isCurrent && <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">Current Week</span>}
                    </div>
                    <div className="p-4 grid gap-3">
                      {weekTasks.map(task => {
                        const project = projects.find(p => p.id === task.project_id);
                        return (
                          <div 
                            key={task.id}
                            className="flex items-center gap-4 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group"
                            data-testid={`timeline-task-${task.id}`}
                          >
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${STATUS_CONFIG[task.status]?.color.split(' ')[0]}`}></div>
                            <div 
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => openTaskPanel(task, 'view')}
                            >
                              <p className="font-medium text-stone-900 truncate">{task.title}</p>
                              <p className="text-xs text-stone-500 truncate">{project?.name || 'Unknown Project'}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-medium text-stone-700">₹{(task.task_cost || 0).toLocaleString()}</p>
                              {task.deadline && (
                                <p className="text-xs text-stone-400">{formatDate(task.deadline)}</p>
                              )}
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_CONFIG[task.status]?.color}`}>
                              {STATUS_CONFIG[task.status]?.label}
                            </span>
                            {/* Chat Button - Navigate to project chat with task context */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${task.project_id}?tab=chat&task=${task.id}`);
                              }}
                              className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Open Project Chat"
                              data-testid={`task-chat-btn-${task.id}`}
                            >
                              <MessageSquare size={18} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Quick Create Project Dialog */}
        <Dialog open={quickProjectDialogOpen} onOpenChange={setQuickProjectDialogOpen}>
          <DialogContent className="max-w-md" data-testid="quick-project-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900">Quick Create Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleQuickCreateProject} className="space-y-4">
              <div>
                <Label className="text-stone-900">Project Name</Label>
                <Input 
                  required 
                  className="bg-white text-stone-900 border-stone-300"
                  value={quickProjectData.name}
                  onChange={(e) => setQuickProjectData({ ...quickProjectData, name: e.target.value })}
                  placeholder="e.g., Sharma Residence"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-900">Start Date</Label>
                  <Input 
                    type="date" 
                    required
                    className="bg-white text-stone-900 border-stone-300"
                    value={quickProjectData.start_date}
                    onChange={(e) => setQuickProjectData({ ...quickProjectData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-stone-900">End Date</Label>
                  <Input 
                    type="date" 
                    required
                    className="bg-white text-stone-900 border-stone-300"
                    value={quickProjectData.end_date}
                    onChange={(e) => setQuickProjectData({ ...quickProjectData, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-stone-900">Budget (₹)</Label>
                <Input 
                  type="number"
                  min="0"
                  className="bg-white text-stone-900 border-stone-300"
                  value={quickProjectData.budget}
                  onChange={(e) => setQuickProjectData({ ...quickProjectData, budget: parseFloat(e.target.value) || 0 })}
                  placeholder="e.g., 500000"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setQuickProjectDialogOpen(false)} className="text-stone-900">
                  Cancel
                </Button>
                <Button type="submit" className="apple-button">
                  Create Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Quick Create Category Dialog */}
        <Dialog open={quickCategoryDialogOpen} onOpenChange={setQuickCategoryDialogOpen}>
          <DialogContent className="max-w-md" data-testid="quick-category-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900">Quick Create Category</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleQuickCreateCategory} className="space-y-4">
              <div>
                <Label className="text-stone-900">Category Code (Auto-generated)</Label>
                <Input 
                  className="bg-stone-100 text-stone-500 border-stone-300"
                  value={quickCategoryData.code}
                  onChange={(e) => setQuickCategoryData({ ...quickCategoryData, code: e.target.value.toUpperCase() })}
                  placeholder="Will be auto-generated"
                  maxLength={5}
                  disabled
                />
                <p className="text-xs text-stone-500 mt-1">Code will be auto-generated by the system</p>
              </div>
              <div>
                <Label className="text-stone-900">Category Name</Label>
                <Input 
                  required 
                  className="bg-white text-stone-900 border-stone-300"
                  value={quickCategoryData.name}
                  onChange={(e) => setQuickCategoryData({ ...quickCategoryData, name: e.target.value })}
                  placeholder="e.g., Kitchen Sink"
                />
              </div>
              <div>
                <Label className="text-stone-900">Group</Label>
                <Select value={quickCategoryData.group} onValueChange={(value) => setQuickCategoryData({ ...quickCategoryData, group: value })}>
                  <SelectTrigger className="bg-white text-stone-900 border-stone-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {categories.map(g => (
                      <SelectItem key={g.group} value={g.group}>
                        {g.group} - {g.group_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-stone-900">Description (Optional)</Label>
                <Textarea 
                  rows={2}
                  className="bg-white text-stone-900 border-stone-300"
                  value={quickCategoryData.description}
                  onChange={(e) => setQuickCategoryData({ ...quickCategoryData, description: e.target.value })}
                  placeholder="Brief description..."
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setQuickCategoryDialogOpen(false)} className="text-stone-900">
                  Cancel
                </Button>
                <Button type="submit" className="apple-button">
                  Create Category
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* AI Task Creation Side Panel */}
        <SidePanel
          isOpen={sidePanelOpen}
          onClose={() => setSidePanelOpen(false)}
          onMinimize={setSidePanelMinimized}
          minimized={sidePanelMinimized}
          title={sidePanelMode === 'create' ? 'Create Task with AI' : sidePanelMode === 'edit' ? 'Edit Task' : selectedTask?.title}
          subtitle={sidePanelMode === 'create' ? 'Smart suggestions as you type' : sidePanelMode === 'view' ? projects.find(p => p.id === selectedTask?.project_id)?.name : undefined}
        >
          {sidePanelMode === 'view' && selectedTask ? (
            // View Mode
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${STATUS_CONFIG[selectedTask.status]?.color || 'bg-stone-100'}`}>
                  {(() => {
                    const IconComponent = STATUS_CONFIG[selectedTask.status]?.icon;
                    return IconComponent ? <IconComponent size={24} /> : <Clipboard size={24} />;
                  })()}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-stone-900">{selectedTask.title}</h3>
                  <p className="text-sm text-stone-500">{STATUS_CONFIG[selectedTask.status]?.label || 'Unknown'}</p>
                </div>
              </div>

              {selectedTask.description && (
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-700">{selectedTask.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 uppercase">Quantity</p>
                  <p className="font-medium text-stone-900">{selectedTask.quantity} {selectedTask.unit}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 uppercase">Rate</p>
                  <p className="font-medium text-stone-900">₹{selectedTask.rate?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 uppercase">Total Cost</p>
                  <p className="font-medium text-stone-900">₹{selectedTask.task_cost?.toLocaleString()}</p>
                </div>
                {selectedTask.deadline && (
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-stone-500 uppercase">Deadline</p>
                    <p className="font-medium text-stone-900">{formatDate(selectedTask.deadline)}</p>
                  </div>
                )}
              </div>

              <SidePanelActions>
                <Button onClick={() => openTaskPanel(selectedTask, 'edit')} className="flex-1">
                  <Pencil size={16} className="mr-2" />
                  Edit Task
                </Button>
              </SidePanelActions>
            </div>
          ) : (
            // Create/Edit Mode with AI Suggestions
            <div className="space-y-4">
              <SidePanelSection>
                <div>
                  <Label className="text-stone-700">Project *</Label>
                  <SearchableCombobox
                    options={[...projects].sort((a, b) => {
                      // Sort by created_at descending (latest first)
                      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                      return dateB - dateA;
                    }).map(p => ({ value: p.id, label: p.name }))}
                    value={formData.project_id}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                    placeholder="Search or select project..."
                    searchPlaceholder="Type to search projects..."
                    allowCreate={user.role === 'owner' || user.role === 'staff'}
                    onCreateNew={() => setQuickProjectDialogOpen(true)}
                    createNewText="+ Create New Project"
                  />
                </div>

                {/* P1 FIX: Required Category Selection */}
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-700">Main Category *</Label>
                    <SearchableCombobox
                      options={categories.map(group => ({
                        value: group.group,
                        label: `${group.group} - ${group.group_name}`,
                      }))}
                      value={formData.category_group || ''}
                      onValueChange={(value) => setFormData({ ...formData, category_group: value, category_code: '' })}
                      placeholder="Select main category..."
                      searchPlaceholder="Search categories..."
                      emptyText="No categories found"
                      allowCreate
                      onCreateNew={() => {
                        setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
                        setQuickAddCategoryDialogOpen(true);
                      }}
                      createNewText="+ Add Main Category"
                      className="mt-1"
                    />
                    {!formData.category_group && (
                      <p className="text-xs text-amber-600 mt-1">Required for BOQ grouping</p>
                    )}
                  </div>
                  {formData.category_group && (
                    <div>
                      <Label className="text-stone-700">Sub-Category</Label>
                      <SearchableCombobox
                        options={(categories.find(g => g.group === formData.category_group)?.categories || []).map(cat => ({
                          value: cat.code,
                          label: `${cat.code} - ${cat.name}`,
                        }))}
                        value={formData.category_code || ''}
                        onValueChange={(value) => setFormData({ ...formData, category_code: value })}
                        placeholder="Select (optional)..."
                        searchPlaceholder="Search sub-categories..."
                        emptyText="No sub-categories"
                        allowCreate
                        onCreateNew={() => {
                          const mainCat = categories.find(g => g.group === formData.category_group);
                          setNewCategoryData({ 
                            name: '', 
                            code: '', 
                            type: 'sub', 
                            parentId: mainCat?.main_category_id || mainCat?.id || ''
                          });
                          setQuickAddCategoryDialogOpen(true);
                        }}
                        createNewText="+ Add Sub-Category"
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <Label className="text-stone-700 flex items-center gap-2">
                    Task Title
                    <Sparkles size={14} className="text-blue-500" />
                    <span className="text-xs text-blue-500 font-normal">AI suggestions enabled</span>
                  </Label>
                  <div className="relative">
                    <Input 
                      value={formData.title} 
                      onChange={(e) => handleTaskNameChange(e.target.value)}
                      onFocus={() => formData.title.length >= 2 && taskSuggestions.length > 0 && setSuggestionsOpen(true)}
                      onBlur={() => setTimeout(() => setSuggestionsOpen(false), 200)}
                      placeholder="Start typing for AI suggestions..."
                      className="mt-1 text-base h-11"
                      style={{ fontSize: '17px' }}
                    />
                    {/* Smart Suggestions Dropdown */}
                    {suggestionsOpen && taskSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                        {taskSuggestions.filter(s => s.source_type === 'previously_used').length > 0 && (
                          <div className="p-2 border-b border-stone-100">
                            <div className="flex items-center gap-2 text-xs font-medium text-stone-500 mb-2">
                              <History size={12} /> Previously Used
                            </div>
                            {taskSuggestions.filter(s => s.source_type === 'previously_used').map((suggestion) => (
                              <div 
                                key={suggestion.id}
                                onClick={() => applySuggestion(suggestion)}
                                className="p-2 hover:bg-blue-50 rounded cursor-pointer"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="font-medium text-stone-900">{suggestion.task_name}</div>
                                  <div className="flex items-center gap-2 text-xs text-stone-400">
                                    <span className="bg-stone-100 px-2 py-0.5 rounded">{suggestion.unit}</span>
                                    <span>{suggestion.usage_count}x used</span>
                                  </div>
                                </div>
                                <div className="text-xs text-stone-500 line-clamp-1 mt-1">{suggestion.description}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {taskSuggestions.filter(s => s.source_type === 'system_suggested').length > 0 && (
                          <div className="p-2">
                            <div className="flex items-center gap-2 text-xs font-medium text-stone-500 mb-2">
                              <Lightbulb size={12} /> System Suggested
                            </div>
                            {taskSuggestions.filter(s => s.source_type === 'system_suggested').map((suggestion) => (
                              <div 
                                key={suggestion.id}
                                onClick={() => applySuggestion(suggestion)}
                                className="p-2 hover:bg-blue-50 rounded cursor-pointer"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="font-medium text-stone-900">{suggestion.task_name}</div>
                                  <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">{suggestion.unit}</span>
                                </div>
                                <div className="text-xs text-stone-500 line-clamp-1 mt-1">{suggestion.description}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-stone-700">Description</Label>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                        className="text-stone-500 hover:text-stone-700 hover:bg-stone-50 h-7 px-2 text-xs"
                        data-testid="expand-description-create-btn"
                      >
                        {descriptionExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => generateAIDescription(false)}
                        disabled={aiDescriptionLoading || !formData.title}
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 px-2 text-xs"
                        data-testid="ai-generate-description-create-btn"
                      >
                        {aiDescriptionLoading ? (
                          <span className="flex items-center gap-1"><span className="animate-spin">⏳</span> Generating...</span>
                        ) : (
                          <span className="flex items-center gap-1"><Wand2 size={14} /> AI Generate</span>
                        )}
                      </Button>
                    </div>
                  </div>
                  <Textarea 
                    value={formData.description} 
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description will auto-fill from AI suggestions..."
                    className={`mt-1 resize-y ${descriptionExpanded ? 'min-h-[300px]' : 'min-h-[120px]'}`}
                    rows={descriptionExpanded ? 12 : 6}
                  />
                  {/* Render formatted description if it contains HTML-like formatting */}
                  {formData.description && (formData.description.includes('**') || formData.description.includes('•') || formData.description.includes('\n-')) && (
                    <div className="mt-2 p-3 bg-stone-50 rounded-lg text-sm text-stone-700 prose prose-sm max-w-none">
                      <p className="text-xs text-stone-500 mb-1">Preview:</p>
                      <div dangerouslySetInnerHTML={{ 
                        __html: formData.description
                          .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
                          .replace(/__(.*?)__/g, '<u>$1</u>')
                          .replace(/\n/g, '<br/>')
                          .replace(/• /g, '&bull; ')
                      }} />
                    </div>
                  )}
                </div>
              </SidePanelSection>

              {/* AI Cost Estimation - Replaces Material Suggestions */}
              {(formData.title || formData.description) && (
                <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-stone-900 flex items-center gap-2">
                      <Sparkles size={14} className="text-purple-500" />
                      AI Cost Estimation
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateAICostEstimate}
                      disabled={aiCostEstimateLoading || (!formData.title && !formData.description)}
                      className="text-purple-600 border-purple-300 hover:bg-purple-100 h-7 px-3 text-xs"
                      data-testid="ai-estimate-cost-btn"
                    >
                      {aiCostEstimateLoading ? (
                        <span className="flex items-center gap-1"><span className="animate-spin">⏳</span> Estimating...</span>
                      ) : (
                        <span className="flex items-center gap-1"><Wand2 size={14} /> Estimate Cost</span>
                      )}
                    </Button>
                  </div>
                  {aiCostEstimate && (
                    <div className="mt-2 p-2 bg-white rounded border border-purple-100">
                      {aiCostEstimate.min_rate > 0 && aiCostEstimate.max_rate > 0 ? (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-stone-600">Estimated Range:</span>
                            <span className="font-medium text-purple-700">
                              ₹{aiCostEstimate.min_rate?.toLocaleString()} - ₹{aiCostEstimate.max_rate?.toLocaleString()} per {formData.unit}
                            </span>
                          </div>
                          {aiCostEstimate.suggested_rate > 0 && (
                            <div className="flex items-center justify-between text-sm mt-1">
                              <span className="text-stone-600">Suggested Rate:</span>
                              <span className="font-bold text-green-600">₹{aiCostEstimate.suggested_rate?.toLocaleString()}</span>
                            </div>
                          )}
                          {aiCostEstimate.confidence && (
                            <div className="text-xs text-stone-500 mt-1">
                              Confidence: <span className={aiCostEstimate.confidence === 'high' ? 'text-green-600' : aiCostEstimate.confidence === 'medium' ? 'text-amber-600' : 'text-stone-500'}>{aiCostEstimate.confidence}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-stone-500">{aiCostEstimate.notes || 'Unable to estimate. Please enter rate manually.'}</p>
                      )}
                    </div>
                  )}
                  {!aiCostEstimate && (
                    <p className="text-xs text-stone-500">Click "Estimate Cost" to get AI-powered rate suggestions based on task description.</p>
                  )}
                </div>
              )}

              {/* Attached Materials - Keep but make it secondary */}
              {attachedMaterials.length > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Label className="text-stone-900 flex items-center gap-2 mb-2">
                    <Package size={14} className="text-green-600" />
                    Attached Materials ({attachedMaterials.length})
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {attachedMaterials.map((mat) => (
                      <span key={mat.id} className="text-xs px-2 py-1 bg-green-100 border border-green-300 rounded-full text-green-800 flex items-center gap-1">
                        {mat.name}
                        <button type="button" onClick={() => removeMaterial(mat.id)} className="hover:text-red-600">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <SidePanelDivider />

              <SidePanelSection title="Quantity & Pricing">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-stone-700">Quantity</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      step="0.01" 
                      value={formData.quantity} 
                      onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-700">Unit</Label>
                    <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNIT_OPTIONS.map((unit) => (<SelectItem key={unit} value={unit}>{unit}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-stone-700">Vendor Rate (₹)</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      value={formData._vendorRate ?? formData.vendor_rate ?? formData.rate} 
                      onChange={(e) => {
                        const vr = parseFloat(e.target.value) || 0;
                        setFormData({ ...formData, rate: vr, vendor_rate: vr, _vendorRate: vr });
                      }}
                      className="mt-1"
                    />
                  </div>
                </div>
                {formData.quantity > 0 && (formData._vendorRate || formData.vendor_rate || formData.rate) > 0 && (
                  <div className="p-3 bg-stone-50 rounded-lg mt-3">
                    <span className="text-stone-600">Vendor Total: </span>
                    <span className="font-bold text-purple-700">₹{(formData.quantity * (formData._vendorRate ?? formData.vendor_rate ?? formData.rate)).toLocaleString()}</span>
                  </div>
                )}
              </SidePanelSection>

              <SidePanelSection title="Assignment & Rates">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-700">Assign To Vendor</Label>
                    {formData.project_id && projectVendors.length > 0 ? (
                      <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                        <SelectTrigger className="mt-1" data-testid="assign-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                        <SelectContent>
                          {projectVendors.map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : formData.project_id ? (
                      <p className="text-sm text-amber-600 mt-2">No vendors assigned to this project. Assign vendors in Project Settings first.</p>
                    ) : (
                      <p className="text-sm text-stone-500 mt-2">Select a project first to see assignable vendors.</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-stone-700">Deadline</Label>
                    <Input 
                      type="date" 
                      value={formData.deadline} 
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                      className="mt-1"
                      data-testid="task-deadline-input"
                    />
                  </div>
                </div>
                
                {/* Rate Information - Shown when editing existing tasks */}
                {sidePanelMode === 'edit' && selectedTask && (selectedTask.vendor_rate || selectedTask.rate) > 0 && (
                  <div className="mt-4 p-3 bg-stone-50 rounded-lg space-y-2">
                    <p className="text-xs font-semibold text-stone-600 uppercase">Rate Breakdown (Forward Markup)</p>
                    <div className="grid grid-cols-3 gap-2 text-sm text-center">
                      {(() => {
                        const vr = selectedTask.vendor_rate || selectedTask.rate || 0;
                        const proj = projects.find(p => p.id === selectedTask.project_id);
                        const adminMk = selectedTask.admin_markup_percentage ?? proj?.admin_markup_percentage ?? proj?.vendor_markdown_percentage ?? 20;
                        const clientMk = selectedTask.client_markup_percentage ?? proj?.client_markup_percentage ?? proj?.markup_percentage ?? 20;
                        const ar = Math.round(vr * (1 + adminMk / 100) * 100) / 100;
                        const cr = Math.round(ar * (1 + clientMk / 100) * 100) / 100;
                        const qty = selectedTask.quantity || 1;
                        const vAmt = Math.round(vr * qty * 100) / 100;
                        const aAmt = Math.round(ar * qty * 100) / 100;
                        const cAmt = Math.round(cr * qty * 100) / 100;
                        return (
                          <>
                            <div className="bg-purple-50 rounded p-2 border border-purple-100">
                              <span className="text-purple-600 block text-xs">Vendor Rate</span>
                              <span className="font-semibold text-purple-700">₹{vr.toLocaleString()}</span>
                              <span className="block text-[10px] text-purple-500 mt-0.5">Amt: ₹{vAmt.toLocaleString()}</span>
                            </div>
                            <div className="bg-stone-100 rounded p-2 border border-stone-200">
                              <span className="text-stone-600 block text-xs">Admin Rate (+{adminMk}%)</span>
                              <span className="font-semibold">₹{ar.toLocaleString()}</span>
                              <span className="block text-[10px] text-stone-500 mt-0.5">Amt: ₹{aAmt.toLocaleString()}</span>
                            </div>
                            <div className="bg-green-50 rounded p-2 border border-green-100">
                              <span className="text-green-600 block text-xs">Client Rate (+{clientMk}%)</span>
                              <span className="font-semibold text-green-700">₹{cr.toLocaleString()}</span>
                              <span className="block text-[10px] text-green-500 mt-0.5">Amt: ₹{cAmt.toLocaleString()}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </SidePanelSection>

              <SidePanelActions>
                {sidePanelMode === 'create' && (
                  <Button variant="outline" onClick={handleCreateHeaderFromAI} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="ai-panel-add-header-btn">
                    <LayoutDashboard size={14} className="mr-1" /> Add Header
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSidePanelOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={sidePanelMode === 'create' ? handleSubmit : handleUpdateTask} 
                  className="flex-1 apple-button"
                >
                  {sidePanelMode === 'create' ? 'Create Task' : 'Save Changes'}
                </Button>
              </SidePanelActions>
            </div>
          )}
        </SidePanel>

        {/* Assign Vendor Dialog */}
        <Dialog open={assignVendorDialogOpen} onOpenChange={setAssignVendorDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus size={20} />
                Assign Vendor to Task
              </DialogTitle>
            </DialogHeader>
            
            {taskToAssign && (
              <div className="space-y-4">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="font-medium text-stone-800">{taskToAssign.title}</p>
                  <p className="text-sm text-stone-500 mt-1">
                    Base Rate: ₹{(taskToAssign.rate || taskToAssign.task_cost || 0).toLocaleString()}
                  </p>
                </div>
                
                <div>
                  <Label>Select Vendor</Label>
                  {availableVendorsForTask.length > 0 ? (
                    <Select value={selectedVendorId} onValueChange={setSelectedVendorId}>
                      <SelectTrigger className="mt-1" data-testid="assign-vendor-select">
                        <SelectValue placeholder="Choose a vendor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableVendorsForTask.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.name} ({vendor.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                      No vendors assigned to this project. Go to Project Settings to assign vendors first.
                    </p>
                  )}
                </div>
                
                <p className="text-xs text-stone-500">
                  Vendor rate will be calculated automatically based on project settings.
                </p>
                
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setAssignVendorDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAssignVendor} 
                    disabled={!selectedVendorId}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-assign-vendor"
                  >
                    <UserPlus size={16} className="mr-2" />
                    Assign Vendor
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* P0: Client Approval Dialog */}
        <Dialog open={clientApprovalDialogOpen} onOpenChange={setClientApprovalDialogOpen}>
          <DialogContent className="max-w-md" data-testid="client-approval-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                <ShieldCheck className="text-purple-500" size={20} />
                Require Client Approval
              </DialogTitle>
            </DialogHeader>
            
            {taskForApproval && (
              <div className="space-y-4">
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <h4 className="font-semibold text-stone-800">{taskForApproval.title}</h4>
                  <p className="text-sm text-stone-500 mt-1">
                    {projects.find(p => p.id === taskForApproval.project_id)?.name || 'Unknown Project'}
                  </p>
                  {taskForApproval.client_approval_status && (
                    <div className="mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        taskForApproval.client_approval_status === 'pending' ? 'bg-orange-100 text-orange-700' :
                        taskForApproval.client_approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                        taskForApproval.client_approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        Current Status: {taskForApproval.client_approval_status}
                      </span>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label>Message to Client (Optional)</Label>
                  <Textarea
                    placeholder="Enter a message for the client explaining what needs approval..."
                    value={approvalMessage}
                    onChange={(e) => setApprovalMessage(e.target.value)}
                    className="mt-1"
                    rows={3}
                    data-testid="approval-message-input"
                  />
                </div>
                
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm text-amber-700">
                    <strong>What happens next:</strong>
                  </p>
                  <ul className="text-xs text-amber-600 mt-1 space-y-1 list-disc pl-4">
                    <li>Task status changes to "Awaiting Client Approval"</li>
                    <li>Client receives a notification on their dashboard</li>
                    <li>Client can approve or reject with remarks</li>
                  </ul>
                </div>
                
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setClientApprovalDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleRequestClientApproval}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="send-approval-request"
                  >
                    <ShieldCheck size={16} className="mr-2" />
                    Send to Client
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* P2: Bulk Assign Vendors Dialog */}
        <Dialog open={bulkAssignVendorsDialogOpen} onOpenChange={setBulkAssignVendorsDialogOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-assign-vendors-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                <Users className="text-amber-500" size={20} />
                Bulk Assign Vendors
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-stone-700">Assigning to:</p>
                <p className="text-stone-900 font-semibold">
                  {selectedTasks.size} task(s) selected
                </p>
              </div>
              
              {bulkAvailableVendors.length === 0 ? (
                <div className="text-center py-6 text-stone-500">
                  <Users size={32} className="mx-auto mb-2 text-stone-300" />
                  <p>No vendors available.</p>
                  <p className="text-sm">Assign vendors to the project(s) first.</p>
                </div>
              ) : (
                <div>
                  <Label className="text-stone-900 mb-2 block">Select Vendors</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3 bg-stone-50">
                    {bulkAvailableVendors.map(vendor => (
                      <div 
                        key={vendor.id} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          bulkSelectedVendorIds.includes(vendor.id) 
                            ? 'bg-amber-100 border border-amber-300' 
                            : 'bg-white border border-stone-200 hover:bg-stone-100'
                        }`}
                        onClick={() => toggleBulkVendorSelection(vendor.id)}
                        data-testid={`bulk-vendor-option-${vendor.id}`}
                      >
                        <Checkbox
                          checked={bulkSelectedVendorIds.includes(vendor.id)}
                          onCheckedChange={() => toggleBulkVendorSelection(vendor.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-stone-900">{vendor.name}</p>
                          {vendor.company && (
                            <p className="text-xs text-stone-500">{vendor.company}</p>
                          )}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                          {vendor.role}
                        </span>
                      </div>
                    ))}
                  </div>
                  {bulkSelectedVendorIds.length > 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      {bulkSelectedVendorIds.length} vendor(s) will be assigned to all selected tasks
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setBulkAssignVendorsDialogOpen(false)} className="text-stone-900">
                Cancel
              </Button>
              <Button 
                onClick={handleBulkAssignVendors} 
                disabled={bulkSelectedVendorIds.length === 0}
                className="apple-button"
                data-testid="bulk-assign-confirm-btn"
              >
                Assign to {selectedTasks.size} Tasks
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* iter-386u-e — Assign Vendor by Category dialog */}
        <Dialog open={assignByCategoryOpen} onOpenChange={setAssignByCategoryOpen}>
          <DialogContent className="max-w-md" data-testid="assign-by-category-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-stone-900 flex items-center gap-2">
                <Layers className="text-amber-500" size={20} />
                Assign Vendor by Category
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                <p className="text-stone-700">
                  Inferred from your selection:
                </p>
                <p className="text-stone-900 font-semibold mt-1">
                  {selectedTasksCategories.length} categor{selectedTasksCategories.length === 1 ? 'y' : 'ies'}
                  {' · '}
                  {selectedTasksArr.length} task{selectedTasksArr.length === 1 ? '' : 's'} in scope
                </p>
                <p className="text-[11px] text-stone-500 mt-1">
                  Vendor will be assigned to every task under these categories (existing + future).
                </p>
              </div>
              {selectedTasksCategories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedTasksCategories.map((c) => (
                    <span key={c.id} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                      {c.name}
                    </span>
                  ))}
                </div>
              )}
              <div>
                <Label className="text-stone-900 mb-2 block">Vendor</Label>
                {bulkAvailableVendors.length === 0 ? (
                  <p className="text-xs text-stone-500 italic">Assign vendors to the project(s) first, then retry.</p>
                ) : (
                  <select
                    value={categoryAssignVendorId}
                    onChange={(e) => setCategoryAssignVendorId(e.target.value)}
                    className="w-full h-10 border border-stone-300 rounded-md px-2 text-sm"
                    data-testid="assign-by-category-vendor-select"
                  >
                    <option value="">Select vendor...</option>
                    {bulkAvailableVendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.name || v.email}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setAssignByCategoryOpen(false)}>Cancel</Button>
              <Button
                onClick={runAssignByCategory}
                disabled={categoryAssignSaving || !categoryAssignVendorId}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="assign-by-category-confirm-btn"
              >
                {categoryAssignSaving ? 'Assigning…' : 'Assign to Categories'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Add Category Dialog */}
        <Dialog open={quickAddCategoryDialogOpen} onOpenChange={setQuickAddCategoryDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-lg flex items-center gap-2">
                <Tags size={18} />
                {newCategoryData.type === 'main' ? 'Add Main Category' : 'Add Sub-Category'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {newCategoryData.type === 'main' ? (
                <>
                  <div>
                    <Label className="text-stone-700">Category Code *</Label>
                    <Input 
                      value={newCategoryData.code} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, code: e.target.value.toUpperCase() })} 
                      placeholder="e.g., A, B, C..."
                      maxLength={3}
                      className="mt-1"
                    />
                    <p className="text-xs text-stone-500 mt-1">Single letter or short code</p>
                  </div>
                  <div>
                    <Label className="text-stone-700">Category Name *</Label>
                    <Input 
                      value={newCategoryData.name} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })} 
                      placeholder="e.g., Civil Works, Electrical..."
                      className="mt-1"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-stone-700">Sub-Category Name *</Label>
                    <Input 
                      value={newCategoryData.name} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })} 
                      placeholder="e.g., Wiring, Switches..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-700">Code (optional)</Label>
                    <Input 
                      value={newCategoryData.code} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, code: e.target.value })} 
                      placeholder="Auto-generated if empty"
                      className="mt-1"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setQuickAddCategoryDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleQuickAddCategoryFromDialog} className="apple-button">
                <Plus size={16} className="mr-2" />Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
